import React, { Component } from 'react';
import { publishFacts, clearFacts } from '../agent/bailey/skills/pageFacts';
import { markFirstTask } from '../onboarding/firstTaskRegistry';
import ReactDOM from 'react-dom';
import SlidePanel from '../shared/SlidePanel';
import { bookingService } from '../../_services/booking.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { formService } from '../../_services/form.service';
import { bookingActions } from '../../_actions';
import { formatTime } from './DiaryContext';
import { BlockIcon } from '../../assets/SVG';
import toast from '../shared/Toast';
import DiaryDatePicker from '../Diary/DiaryDatePicker';
import ShiftTimePicker from '../Diary/ShiftTimePicker';
import DurationPicker from '../Diary/DurationPicker';
import StaffPicker from '../Diary/StaffPicker';
import ClientDetailPanel from '../clients/ClientDetailPanel';
import AddClientPanel from '../clients/AddClientPanel';
import BookingPhotosInline from './BookingPhotosInline';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import { loadScheme as loadLoyaltyScheme, currentOwnerId as loyaltyOwnerId, hydrateLoyalty } from '../loyalty-v2/loyaltyStore';
import { computeBookingWindow, bookingSegments, applySegmentPositions } from '../shared/computeBookingWindow';
import moment from 'moment';
import { REFUNDS_VISIBLE, CLIENT_PHOTOS_VISIBLE } from '../../config/featureFlags';
import RefundPanel from '../refunds/RefundPanel';
import RefundBadge from '../refunds/RefundBadge';
import { canOfferRefund, refundStatusOf } from '../refunds/refundLogic';
import { canRefundDeposit, canChargeNoShow, canSeeSaleRefunds } from '../permissions/access';
import { isHidden as isReceptionHidden } from '../../_utils/receptionMode';
import '../shared/ui.css';
import './BookingDetailPanel.css';
import { bookingSeriesService } from '../../_services/bookingSeries.service';
import { cancelScopeSaid, visitsAround, cancelScopes, cancelWords, scopeDetail, editWords } from './repeat/repeat';
import { ScopeOptions, ScopeSwitch, DateCheck, RepeatControl } from './repeat/RepeatControl';
import { needsCheck, bookedCount, choiceFor } from './repeat/repeat';
import { PAUSE_PICKS, pauseUntil, pauseProblem, visitsToPause, pauseWords, repeatFromSeries, sameRepeat, withoutOwnVisits, ownVisitIds, changeRuleBody, endIsValid, repeatSaid, dateSaid, ruleFor } from './repeat/repeat';
import { canUndo, undoLeft, undoLeftSaid } from './repeat/undo';
import { undoRepeat } from './repeat/UndoRepeat';
import { affectedFrom, savedWords } from './repeat/affected';
import { loadSpan, laterVisitsOf, laterClashes } from './repeat/followingCheck';
import { startPlacement, cancelPlacement, setCheckingDates } from './repeat/placement';
import './repeat/Repeat.css';

// Money actually taken, from the transactions collection rows. Same rule as
// checkout (useCheckoutState): succeeded payments only, never the
// "cashCollected" pay-at-the-salon marker a salon-made booking writes. Counting
// that marker showed "Deposit Already Paid" on unpaid bookings and capped the
// no-show fee at £0 (The Salon Letchworth, 2026-10-02).
// Why there's no card to charge, in the salon's terms. The old copy told every
// salon to "enable no show protection in booking settings", even salons that
// had it on — when the real reason was a booking made in the salon for a client
// Beu couldn't ask for a card (no email), as at The Salon Letchworth, 2026-10-02.
function noCardReason(detail) {
  const d = detail || {};
  const u = d.user || {};
  const first = String(u.first_name || u.name || 'the client').trim().split(/\s+/)[0];
  const email = String(u.email || '');
  const hasEmail = !!email && !/@beuapp\.com$/i.test(email);
  const askedForCard = Number(d.hairdresserCardCapture) === 1;
  if (Number(d.isOnlineBooking) === 1) {
    return askedForCard
      ? `No card on file. ${first} booked online but didn't save a card, so there's nothing to charge.`
      : 'No card on file. No show protection was off when this was booked, so the client wasn\'t asked for a card. Turn it on in Booking settings and Beu will ask every client.';
  }
  if (!askedForCard) {
    return 'No card on file. No show protection was off when this was booked, so the client wasn\'t asked for a card. Turn it on in Booking settings and Beu will ask every client.';
  }
  return hasEmail
    ? `No card on file. This booking was made in the salon and ${first} hasn't added a card, so there's nothing to charge.`
    : `No card on file. This booking was made in the salon and there's no email address for ${first}, so Beu couldn't ask them for a card. Add their email to their client profile and Beu will ask next time you book them.`;
}

function paidFromTransactions(list) {
  return (list || []).reduce((sum, t) => (
    t && t.pgTransactionStatus === 'succeeded' && t.pgObjectType !== 'cashCollected'
      ? sum + (parseFloat(t.pgTransactionAmount) || 0)
      : sum
  ), 0);
}

// The tip is taken on the card leg (reader / tap to pay). Returns the tip when
// this row is the last card leg of the sale, else 0, so it's noted once.
function tipOnLeg(t, list, tip) {
  if (!(tip > 0)) return 0;
  const cardLegs = (list || []).filter(x => [2, 5, 10, 15].includes(x.paymentType));
  return cardLegs.length && cardLegs[cardLegs.length - 1] === t ? tip : 0;
}

// Client photos: per env via CLIENT_PHOTOS_VISIBLE (see ClientDetailPanel).
const CLIENT_PHOTOS_ENABLED = CLIENT_PHOTOS_VISIBLE;

// Walk-in client ID used across the platform
const WALKIN_CLIENT_ID = '64f5c7789a069cda0188fea3';

/**
 * Decide whether a booking was made online by the client themselves vs in-salon
 * by a stylist / salon owner. The canonical signal is `isOnlineBooking`, which
 * the backend sets explicitly on create — 1 via createOnlineBooking, 0 via
 * createAdminBooking.
 *
 * The flag is reliably present on LIST payloads (diary feed, client bookings),
 * but the by-id detail call has not consistently echoed it (see the commit that
 * introduced this helper: "the first booking-detail call we inspected didn't
 * echo any of the obvious flags"). So we read it from the detail when it is
 * actually there, and otherwise fall back to the value carried on the event
 * the panel was opened from, which came from a list payload. That is a real
 * copy of the same flag, not an inference.
 *
 * We deliberately do NOT infer from `createdBy === userId`: the admin/in-salon
 * create path sets `createdBy = resolvedClientId` (the client), which equals the
 * booking's own `userId`, so ~90% of in-salon bookings matched that heuristic and
 * were mislabelled "Online". `bookingType === 1` is a dead legacy fallback (the
 * field isn't present on current bookings) kept only for very old responses.
 */
function isOnlineBookingDetail(detail, event) {
  const isSet = (v) => v !== undefined && v !== null;
  // BE writes 1 / 0; tolerate boolean and stringified forms defensively.
  const isTruthyFlag = (v) => v === 1 || v === true || v === '1';

  if (detail && isSet(detail.isOnlineBooking)) return isTruthyFlag(detail.isOnlineBooking);
  if (event && isSet(event.isOnlineBooking)) return isTruthyFlag(event.isOnlineBooking);
  if (detail && detail.bookingType === 1) return true;
  return false;
}

// Canonical status map matching V1 (CalenderModule / DashboardBookings)
const STATUS_MAP = {
  0: { label: 'Pending',     css: 'bdp-status--pending' },
  1: { label: 'Booked',      css: 'bdp-status--booked' },
  2: { label: 'Confirmed',   css: 'bdp-status--confirmed' },
  3: { label: 'Cancelled',   css: 'bdp-status--cancelled' },
  4: { label: 'Completed',   css: 'bdp-status--completed' },
  5: { label: 'Rescheduled', css: 'bdp-status--rescheduled' },
  6: { label: 'No Show',     css: 'bdp-status--noshow' },
};

function getStatus(code) {
  return STATUS_MAP[code] || { label: 'Unknown', css: '' };
}

/**
 * BookingDetailPanel - Slide-out panel showing booking/event/holiday details.
 * Desktop: slides from right. Mobile: slides up from bottom.
 * Uses the shared SlidePanel component (custom layout mode).
 */
class BookingDetailPanel extends Component {
  constructor(props) {
    super(props);
    this.slidePanelRef = React.createRef();
    this._menuRef = React.createRef();
    this.state = {
      isLoading: true,
      bookingDetail: null,
      error: null,
      menuOpen: false,
      showBookingInfo: false,
      // Navigation stack for viewing other bookings within the panel
      panelHistory: [], // stack of { bookingDetail, event } for back navigation
      // Tab state
      activeTab: 'details', // 'details' | 'client' | 'activity'
      // Refund a completed sale (docs/refunds.md): the panel over this one.
      refundOpen: false,
      clientData: null,
      clientLoading: false,
      clientError: false,
      // Wallet: the salon's loyalty scheme (hydrated fresh, not the cold
      // cache) + this client's real progress card on it.
      loyaltyScheme: null,
      loyaltySchemeLoaded: false,
      loyaltyClientCard: null,
      loyaltyCardLoaded: false,
      clientPanelOpen: false,
      clientPanelInitialTab: null,
      // A repeat: pause between two dates, and change how often
      pauseFrom: '',
      pauseTo: '',
      pauseOpen: null, // 'from' | 'to' while a date picker is open
      ruleEdit: null, // { repeat, before, check, choices, times, busy }
      undoing: false,
      showAddClientFromEdit: false,
      // Room pin/reassign (docs/resources.md §5a)
      roomOptions: null,        // fetched on expand
      roomExpanded: null,       // groupId whose options are open
      roomSaving: false,
      pastBookingsVisible: 5,
      // Edit event/holiday
      editEventMode: false,
      editEventTitle: '',
      editEventDate: '',
      editEventEndDate: '',
      editEventStartMinutes: 0,
      editEventEndMinutes: 0,
      editEventColour: '#29282D',
      editEventBookingAllowed: false,
      editEventSaving: false,
      // Send form modal
      showSendForm: false,
      availableForms: [],
      availableFormsLoading: false,
      sendingFormId: null,
      // Edit client
      editClientId: null,
      editClientName: '',
      editClientEmail: '',
      editClientPhone: '',
      editClientSearchQuery: '',
      editClientResults: [],
      editClientSearching: false,
      editClientPage: 1,
      editClientHasMore: true,
      // Note editing
      editingClientNote: false,
      editingBookingNote: false,
      clientNoteValue: '',
      bookingNoteValue: '',
      noteSaving: false,
      // Cancel/NoShow overlay state
      activeOverlay: null, // 'cancel' | 'noshow' | null
      overlayClosing: false,
      actionLoading: false,
      // No-show specific
      noShowSelectedOption: 2, // 1 = charge card, 2 = no charge
      noShowFeeAmount: '0.00',
      noShowErrorStatus: false,
      // Edit mode
      editMode: false,
      editDate: '',
      editStartMinutes: 0,
      editEndMinutes: 0,
      editStaffId: '',
      editSaving: false,
      notifyEmail: false,
      datePickerOpen: false,
      // Service editing
      editServices: [],
      availableServices: [], // grouped by category from API
      editScreen: 'form', // 'form' | 'servicePicker'
      serviceSearchQuery: '',
      replacingServiceIndex: null, // index in editServices being swapped, or null for add
      servicesLoading: false,
    };
  }

  componentDidMount() {
    this.fetchDetail();
    document.addEventListener('mousedown', this.handleOutsideClick);
    // If opened via context menu with a pre-selected action, trigger it after detail loads
    this._pendingAction = this.props.initialAction || null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.event?.bookingId !== this.props.event?.bookingId ||
        prevProps.event?.eventId !== this.props.event?.eventId) {
      this.setState({ error: null, menuOpen: false });
      this.fetchDetail();
    }
    // Handle drop/resize overrides on the same booking (panel already open)
    // Handle drop/resize overrides (check both _dropOverrides and sessionStorage fallback)
    const newDrop = this.props.event?._dropOverrides;
    const oldDrop = prevProps.event?._dropOverrides;
    if (newDrop && newDrop !== oldDrop) {
      this.handleDropEdit();
    } else if (this.props.initialAction === 'drop' && prevProps.initialAction !== 'drop') {
      this.handleDropEdit();
    }
  }

  componentWillUnmount() {
    // Closed mid-check: nothing stays waiting on the diary
    if (this.state.followCheck || (this.state.ruleEdit && this.state.ruleEdit.check)) setCheckingDates(false);
    cancelPlacement({ quiet: true });
    document.removeEventListener('mousedown', this.handleOutsideClick);
    this._unmounted = true;
    clearFacts('booking-open');
  }

  // Tell Bailey which booking is open, so "move this to 3pm" or "who's this
  // with?" asked from the pet means this one. Only what the panel shows.
  _tellBailey(booking) {
    try {
      if (!booking || !booking._id) return;
      const who = booking.clientInfo?.name || booking.user?.name || 'Walk-in';
      const what = (booking.servicesBooked || []).map(s => s.serviceName).filter(Boolean).join(' + ') || 'an appointment';
      const withWhom = booking.hairdresserInfo?.name ? ` with ${booking.hairdresserInfo.name}` : '';
      const when = booking.bookingDate ? ` on ${moment(booking.bookingDate).format('ddd D MMM, HH:mm')}` : '';
      const label = `${who}, ${what}${withWhom}${when}`;
      publishFacts('booking-open', {
        path: `/${(window.location.pathname.split('/')[1] || '')}`,
        lines: [`Booking open: ${label}`],
        entity: { type: 'booking', id: String(booking._id), label },
      });
    } catch (e) { /* only for Bailey */ }
  }

  handleOutsideClick = (e) => {
    if (this.state.menuOpen && this._menuRef.current && !this._menuRef.current.contains(e.target)) {
      this.setState({ menuOpen: false });
    }
  }

  fetchDetail() {
    const { event } = this.props;
    if (!event) return;

    if (event.type === 'booking' && event.bookingId) {
      this.setState({ isLoading: true, error: null });
      bookingService.getBookingByIdV2(event.bookingId)
        .then(response => {
          // V2 API returns { data: { responseData: booking } } with clientInfo/hairdresserInfo
          const raw = response?.data?.responseData;
          if (!raw) {
            this.setState({ isLoading: false, error: 'Booking not found' });
            return;
          }
          // Map V2 response to legacy-compatible shape (same as booking.actions.js)
          const booking = {
            ...raw,
            user: raw.clientInfo,
            userId: raw.clientInfo?._id,
            hairdresser: raw.hairdresserInfo ? {
              ...raw.hairdresserInfo,
              hairdresser: raw.hairdresserInfo.hairdresser,
            } : null,
            hairdresserId: raw.hairdresserInfo?._id,
          };
          this.setState({ bookingDetail: booking, isLoading: false, series: null, cancelScope: 'this' }, () => {
            this._tellBailey(raw);
            this._executePendingAction();
            this._loadSeries(raw.seriesId);
          });
        })
        .catch(() => {
          this.setState({ isLoading: false, error: 'Could not load booking details' });
        });
    } else {
      // Events, holidays, breaks use the data we already have
      this.setState({ isLoading: false, error: null }, () => {
        this._executePendingAction();
      });
    }
  }

  // After a refund: refetch the booking on screen (stacked or not) so the
  // badge and the Refunded line show, and let the diary refresh too.
  handleRefunded = () => {
    const id = this.state.bookingDetail && this.state.bookingDetail._id;
    if (this.props.onRefresh) this.props.onRefresh();
    if (!id) return;
    bookingService.getBookingByIdV2(id).then((response) => {
      const raw = response && response.data && response.data.responseData;
      if (!raw || this._unmounted) return;
      if (!this.state.bookingDetail || this.state.bookingDetail._id !== id) return;
      this.setState({
        bookingDetail: {
          ...raw,
          user: raw.clientInfo,
          userId: raw.clientInfo && raw.clientInfo._id,
          hairdresser: raw.hairdresserInfo ? { ...raw.hairdresserInfo, hairdresser: raw.hairdresserInfo.hairdresser } : null,
          hairdresserId: raw.hairdresserInfo && raw.hairdresserInfo._id,
        },
      });
    }).catch(() => {});
  }

  _executePendingAction() {
    const action = this._pendingAction;
    this._pendingAction = null;
    if (!action) return;

    if (action === 'cancel') {
      this.handleCancelBooking();
    } else if (action === 'noshow') {
      this.handleNoShow();
    } else if (action === 'edit') {
      this.handleEdit();
    } else if (action === 'drop') {
      this.handleDropEdit();
    } else if (action === 'editEvent') {
      this.handleEditEventStart();
    } else if (action === 'deleteEvent') {
      this.handleDeleteEvent();
    } else if (action === 'message') {
      // From time off landing on this booking: the client's Messages
      this.setState({ clientPanelOpen: true, clientPanelInitialTab: 'messages' });
    }
  }

  handleCheckout = () => {
    const { event, history, onOpenCheckout } = this.props;
    if (!event?.bookingId) return;
    const siteSetting = require('../../config/env').default;
    if (siteSetting.useNewCheckout && onOpenCheckout) {
      onOpenCheckout(event.bookingId);
    } else {
      history.push({ pathname: '/checkout/' + event.bookingId, state: { pageLoadingStatus: 1 } });
    }
  }

  handleEdit = () => {
    const { event } = this.props;
    const { bookingDetail } = this.state;
    if (!event) return;

    const detail = bookingDetail || {};
    const staffId = detail.hairdresserId || event.staffId;

    const rawServices = (detail.servicesBooked || event.services || []).map(s => ({ ...s }));
    const eventServices = event.services || [];

    // Fix any services with missing/negative durations; ensure each has a hairdresserId
    const services = rawServices.map((s, i) => {
      // Preserve processingTime from normalizer if the API response lacks it
      if ((s.processingTime == null || s.processingTime === 0) && eventServices[i]?.processingTime > 0) {
        s.processingTime = eventServices[i].processingTime;
      }
      if (s.finishingTime == null) {
        s.finishingTime = eventServices[i]?.finishingTime ?? 0;
      }
      // Resolve setup/cleanup overrides — booking-entry first, then the
      // service-definition default, then 0. Same pattern as processingTime.
      if (s.setupTime == null) {
        s.setupTime = eventServices[i]?.setupTime ?? s.service?.setupTime ?? 0;
      }
      if (s.cleanupTime == null) {
        s.cleanupTime = eventServices[i]?.cleanupTime ?? s.service?.cleanupTime ?? 0;
      }
      // Set per-service staff (fall back to booking-level staff)
      if (!s.hairdresserId) {
        s.hairdresserId = s.hairdresser?.hairdresserId || s.hairdresser?._id || staffId;
        s.hairdresserName = s.hairdresser?.name || '';
      }
      if (!s.serviceTime || s.serviceTime <= 0) {
        s.serviceTime = s.service?.serviceTime || eventServices[i]?.serviceTime || 30;
      }
      return s;
    });

    // Use the booking-level slot bounds, not the clicked segment's bounds.
    // For a chain segment booking (multi-service with processing gaps),
    // event.startMinutes/endMinutes describes the *segment* the user clicked,
    // not the booking. Edit panel needs the booking's bounds so per-service
    // timing renders correctly from the start of service 1.
    const bookingStart = (detail.bookingSlot && detail.bookingSlot.length >= 1)
      ? detail.bookingSlot[0]
      : event.startMinutes;
    const bookingEnd = (detail.bookingSlot && detail.bookingSlot.length >= 2)
      ? detail.bookingSlot[1]
      : event.endMinutes;

    // Infer processing time from slot gap if not explicitly set
    if (services.length > 1 && !services.some(s => (s.processingTime || 0) > 0)) {
      const totalSvcTime = services.reduce((sum, s) => sum + (s.serviceTime || 0), 0);
      const slotDuration = bookingEnd - bookingStart;
      if (slotDuration > totalSvcTime && totalSvcTime > 0) {
        const gap = slotDuration - totalSvcTime;
        const gapPerService = Math.round(gap / (services.length - 1));
        services.forEach((s, i) => { if (i < services.length - 1) s.processingTime = gapPerService; });
      }
    }

    this.setState({
      editMode: true,
      menuOpen: false,
      editScope: 'this', // a repeat: each edit starts on "This visit"
      editDate: detail.bookingDate || event.date || this.props.bookingDate,
      editStartMinutes: bookingStart,
      editEndMinutes: bookingEnd,
      editStaffId: staffId,
      editServices: services,
      editScreen: 'form',
      serviceSearchQuery: '',
      replacingServiceIndex: null,
      editClientId: detail.user?._id || detail.userId || event.clientId,
      editClientName: detail.user?.name || event.clientName || '',
      editClientEmail: detail.user?.email || '',
      editClientPhone: detail.user?.phoneNumber || '',
    });

    // Pass staffId directly — setState above hasn't flushed yet.
    this.loadAvailableServices(staffId);
  }

  /**
   * Handle drop-to-edit: enters edit mode with the drop target's staff/time pre-applied.
   * Loads the target stylist's services, then removes any booking services they don't offer.
   */
  handleDropEdit = () => {
    const { event } = this.props;
    const { bookingDetail } = this.state;
    if (!event) return;

    let drop = event._dropOverrides;
    // Fallback: read resize data from sessionStorage if _dropOverrides was lost during React re-renders
    if (!drop) {
      try {
        const resizeStr = sessionStorage.getItem('beu-resize');
        if (resizeStr) {
          sessionStorage.removeItem('beu-resize');
          const resizeData = JSON.parse(resizeStr);
          drop = {
            staffId: this.state.editStaffId || event.staffId,
            startMinutes: resizeData.startMinutes,
            endMinutes: resizeData.endMinutes,
            date: this.props.bookingDate || event.date,
            edge: resizeData.edge,
          };
        }
      } catch (e) { /* ignore */ }
    }
    if (!drop) { this.handleEdit(); return; }

    const detail = bookingDetail || {};
    const rawServices = (detail.servicesBooked || event.services || []).map(s => ({ ...s }));

    // Ensure processingTime is preserved from event.services if not on detail
    const eventServices = event.services || [];
    const services = rawServices.map((s, i) => {
      // Preserve processingTime from normalizer if the API response lacks it
      if ((s.processingTime == null || s.processingTime === 0) && eventServices[i]?.processingTime > 0) {
        s.processingTime = eventServices[i].processingTime;
      }
      if (s.finishingTime == null) {
        s.finishingTime = eventServices[i]?.finishingTime ?? 0;
      }
      if (!s.hairdresserId) {
        s.hairdresserId = s.hairdresser?.hairdresserId || s.hairdresser?._id || drop.staffId;
        s.hairdresserName = s.hairdresser?.name || '';
      }
      // Point all services to the new stylist
      s.hairdresserId = drop.staffId;
      if (!s.serviceTime || s.serviceTime <= 0) {
        s.serviceTime = s.service?.serviceTime || eventServices[i]?.serviceTime || 30;
      }
      return s;
    });

    // If segment positions from diary are provided, decide whether the user
    // moved a segment (drag) or resized one (drag an edge). For a *move*,
    // serviceTime must be preserved — mid-drag optimistic clamping can
    // collapse positions to nonsense (e.g. dragging seg 2 over seg 1
    // produced 5min/30proc/5min instead of the expected 30+0+30). For a
    // *resize*, the segment durations are the user's intent — propagate
    // them to serviceTime. The handleEventResize chain branch passes
    // drop.edge ('top'/'bottom') to disambiguate.
    //
    // Blocks map to services via bookingSegments (the same plan the diary
    // drew them from): a service with finishingTime is two blocks — the
    // service, then its finishing — with its processing as the gap between.
    if (drop.segmentPositions && drop.segmentPositions.length === bookingSegments(services).length) {
      applySegmentPositions(services, drop.segmentPositions, !!drop.edge);
    } else {
      // Drag-resize on a single-block booking (no processing gap rendering).
      // Adjust ONLY the service whose edge was actually dragged — top edge
      // adjusts first service, bottom edge adjusts last service. No cascade
      // into other services: previously a -50min drag would cascade-shrink
      // every service to the 5-min floor, producing nonsense like 5+30proc+5.
      //
      // drop.startMinutes / drop.endMinutes are the new visual slot bounds —
      // i.e. they INCLUDE setup + cleanup (the FE widens bookingSlot to wrap
      // the buffer windows). originalTotal must include the same so delta
      // represents the actual user-intended change. Without setup + cleanup
      // in originalTotal, delta = setup + cleanup on a no-op drag and the
      // serviceTime gets +setup+cleanup minutes added every time.
      const newSlotDuration = drop.endMinutes - drop.startMinutes;
      // setup + services + processing gaps + finishing + cleanup
      const originalTotal = computeBookingWindow(services).totalSlotMinutes;
      const delta = newSlotDuration - originalTotal;

      if (delta !== 0 && services.length > 0) {
        if (drop.edge === 'top') {
          services[0].serviceTime = Math.max(5, (services[0].serviceTime || 30) + delta);
        } else {
          const last = services.length - 1;
          services[last].serviceTime = Math.max(5, (services[last].serviceTime || 30) + delta);
        }
      }

      // Infer processing time from remaining slot gap if needed (only when
      // no processing exists — this distributes the gap between services
      // evenly when the drag legitimately added inter-service space).
      if (services.length > 1 && !services.some(s => (s.processingTime || 0) > 0)) {
        const newTotalSvc = services.reduce((sum, s) => sum + (s.serviceTime || 0), 0);
        if (newSlotDuration > newTotalSvc && newTotalSvc > 0) {
          const gap = newSlotDuration - newTotalSvc;
          const gapPerService = Math.round(gap / (services.length - 1));
          services.forEach((s, i) => { if (i < services.length - 1) s.processingTime = gapPerService; });
        }
      }
    }

    this.setState({
      editMode: true,
      menuOpen: false,
      editDate: drop.date,
      editStartMinutes: drop.startMinutes,
      editEndMinutes: drop.endMinutes,
      editStaffId: drop.staffId,
      editServices: services,
      editScreen: 'form',
      serviceSearchQuery: '',
      replacingServiceIndex: null,
      editClientId: detail.user?._id || detail.userId || event.clientId,
      editClientName: detail.user?.name || event.clientName || '',
      editClientEmail: detail.user?.email || '',
      editClientPhone: detail.user?.phoneNumber || '',
    });

    // Load services for the TARGET stylist and validate
    this.loadAvailableServicesForStaff(drop.staffId, services);
  }

  /**
   * Load services for a specific stylist and remove any booked services they don't offer.
   */
  loadAvailableServicesForStaff = (staffId, currentServices) => {
    this.setState({ servicesLoading: true });
    hairdresserService.getServicesGroupedV2({ hairdresserId: staffId })
      .then(response => {
        const categories = response?.data?.data?.hairdresserSelectedServices || [];
        const allCategories = Array.isArray(categories) ? categories : [];
        this.setState({ availableServices: allCategories, servicesLoading: false });

        // Build lookup maps: by service ID and by service name (normalised)
        const offeredServiceIds = new Set();
        const offeredByName = new Map(); // normalised name → service object from target stylist
        allCategories.forEach(cat => {
          (cat.services || []).forEach(svc => {
            if (svc._id) offeredServiceIds.add(svc._id.toString());
            if (svc.serviceId) offeredServiceIds.add(svc.serviceId.toString());
            const name = (svc.serviceName || svc.name || '').trim().toLowerCase();
            if (name && !offeredByName.has(name)) offeredByName.set(name, svc);
          });
        });

        // Check each booked service:
        // 1. If the target stylist has the same service ID → keep as-is
        // 2. If not, try matching by service name → auto-swap to target stylist's version (different price/ID)
        // 3. If no match at all → flag for replacement
        const validated = currentServices.map(s => {
          const svcId = (s.service?.serviceId || s.service?._id || s.serviceId || s._id || '').toString();
          if (svcId && offeredServiceIds.has(svcId)) return s; // exact ID match

          // Try matching by name
          const bookedName = (s.service?.serviceName || s.serviceName || '').trim().toLowerCase();
          const matchedSvc = bookedName ? offeredByName.get(bookedName) : null;

          if (matchedSvc) {
            // Same service, different stylist → swap to their version with their price + time
            // Keep the booking's processingTime (it's per-booking, not per-catalog)
            return {
              ...s,
              service: matchedSvc,
              serviceId: matchedSvc._id || matchedSvc.serviceId,
              _id: matchedSvc._id || matchedSvc.serviceId,
              serviceName: matchedSvc.serviceName || matchedSvc.name,
              servicePrice: matchedSvc.servicePrice,
              serviceTime: s.serviceTime || matchedSvc.serviceTime || 30,
              processingTime: s.processingTime || 0,
              diaryColour: matchedSvc.diaryColour || s.diaryColour,
            };
          }

          // No match — stylist doesn't offer this service
          return { ...s, _needsReplacement: true };
        });

        const hasInvalid = validated.some(s => s._needsReplacement);
        this.setState({ editServices: validated });
      })
      .catch(() => {
        this.setState({ servicesLoading: false });
      });
  }

  loadAvailableServices = (overrideStaffId) => {
    // Accept an explicit staffId so callers can pass it directly when
    // setState hasn't flushed yet. Falling back to the logged-in user
    // returns the WRONG stylist's services on owner accounts (e.g.
    // owner editing another stylist's booking → "No services available").
    const hairdresserId = overrideStaffId || this.state.editStaffId || localStorage.getItem('userId');
    if (!hairdresserId) return;

    this.setState({ servicesLoading: true });
    hairdresserService.getServicesGroupedV2({ hairdresserId })
      .then(response => {
        const categories = response?.data?.data?.hairdresserSelectedServices || [];
        this.setState({ availableServices: Array.isArray(categories) ? categories : [], servicesLoading: false });
      })
      .catch(() => {
        this.setState({ servicesLoading: false });
      });
  }

  handleOpenServicePicker = (index) => {
    // index = which service to replace, null = add new
    this.setState({
      editScreen: 'servicePicker',
      replacingServiceIndex: index,
      serviceSearchQuery: '',
    });
  }

  handleCloseServicePicker = () => {
    this.setState({ editScreen: 'form', serviceSearchQuery: '', replacingServiceIndex: null });
  }

  handleSelectService = (service) => {
    const { replacingServiceIndex, editServices, editStaffId } = this.state;
    // When replacing, keep the existing service's staff; when adding new, inherit booking staff
    const existingStaff = replacingServiceIndex !== null && replacingServiceIndex < editServices.length
      ? editServices[replacingServiceIndex]
      : null;
    const staffId = existingStaff?.hairdresserId || editStaffId;
    const staffName = existingStaff?.hairdresserName || '';

    const colour = service.diaryColour || service.service?.diaryColour || null;
    const newService = {
      service: {
        serviceId: service._id || service.serviceId,
        _id: service._id || service.serviceId,
        serviceName: service.serviceName,
        servicePrice: service.servicePrice,
        diaryColour: colour,
      },
      serviceId: service._id || service.serviceId,
      serviceName: service.serviceName,
      servicePrice: service.servicePrice,
      serviceTime: service.serviceTime || 30,
      processingTime: service.processingTime || 0,
      finishingTime: service.finishingTime || 0,
      diaryColour: colour,
      hairdresserId: staffId,
      hairdresserName: staffName,
    };

    let updated;
    if (replacingServiceIndex !== null && replacingServiceIndex < editServices.length) {
      updated = editServices.map((s, i) => i === replacingServiceIndex ? newService : s);
    } else {
      updated = [...editServices, newService];
    }

    this.setState({
      editServices: updated,
      editScreen: 'form',
      serviceSearchQuery: '',
      replacingServiceIndex: null,
    });
  }

  handleRemoveService = (index) => {
    this.setState(prev => ({
      editServices: prev.editServices.filter((_, i) => i !== index),
    }));
  }

  handleMoveService = (index, direction) => {
    this.setState(prev => {
      const arr = [...prev.editServices];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return null;

      // Swap positions.
      [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];

      // On reorder, re-derive each service's setup/cleanup from its OWN
      // definition (s.service.setupTime / s.service.cleanupTime). Falls back
      // to the entry's existing value if the nested definition isn't on
      // hand. Mid-chain values always zero — multi-service rule.
      //
      // Reasoning: a structural reorder is the user saying "use this new
      // layout's natural buffers". Each service's definition is the
      // salon-friendly default. If a user wants a different value
      // post-reorder, they can override via the picker.
      const newArr = arr.map((s, i) => {
        const defSetup = parseInt(s.service?.setupTime ?? s.setupTime, 10) || 0;
        const defCleanup = parseInt(s.service?.cleanupTime ?? s.cleanupTime, 10) || 0;
        return {
          ...s,
          setupTime: i === 0 ? defSetup : 0,
          cleanupTime: i === arr.length - 1 ? defCleanup : 0,
        };
      });

      return { editServices: newArr };
    });
  }

  handleServiceStaffChange = (index, staffId) => {
    const { staffList } = this.props;
    const staff = staffList?.find(s => s.id === staffId);
    this.setState(prev => ({
      editServices: prev.editServices.map((s, i) =>
        i === index ? { ...s, hairdresserId: staffId, hairdresserName: staff?.name || '' } : s
      ),
    }));
  }

  handleProcessingTimeChange = (index, value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => ({
      editServices: prev.editServices.map((s, i) =>
        i === index ? { ...s, processingTime: mins } : s
      ),
    }));
  }

  handleFinishingTimeChange = (index, value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => ({
      editServices: prev.editServices.map((s, i) =>
        i === index ? { ...s, finishingTime: mins } : s
      ),
    }));
  }

  handleServiceDurationChange = (index, value) => {
    const mins = Math.max(5, parseInt(value) || 5);
    this.setState(prev => ({
      editServices: prev.editServices.map((s, i) =>
        i === index ? { ...s, serviceTime: mins } : s
      ),
    }));
  }

  // Per-appointment override for the buffers. Setup goes on the first
  // service, cleanup on the last. Mid-chain values stay at 0 — enforced
  // again in the save payload.
  handleEditSetupTimeChange = (value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => {
      if (prev.editServices.length === 0) return null;
      return {
        editServices: prev.editServices.map((s, i) => i === 0 ? { ...s, setupTime: mins } : s),
      };
    });
  }

  handleEditCleanupTimeChange = (value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => {
      if (prev.editServices.length === 0) return null;
      const lastIdx = prev.editServices.length - 1;
      return {
        editServices: prev.editServices.map((s, i) => i === lastIdx ? { ...s, cleanupTime: mins } : s),
      };
    });
  }

  getEditTotalDuration() {
    return computeBookingWindow(this.state.editServices).totalSlotMinutes;
  }

  getEditEndMinutes() {
    return this.state.editStartMinutes + this.getEditTotalDuration();
  }

  handleEditFullPage = () => {
    const { event, history } = this.props;
    if (event?.bookingId) {
      history.push('/new-booking-edit/' + event.bookingId);
    }
  }

  handleEditCancel = () => {
    this.setState({ editMode: false, editScreen: 'form' });
  }

  // "This and later" on a repeat: check the later visits at the new time
  // first, like a new repeat. Any clash opens Check the dates here, and the
  // save carries what they chose for each.
  handleEditSave = () => {
    const { event } = this.props;
    const { bookingDetail, editScope } = this.state;
    if (!(bookingDetail && bookingDetail.seriesId && editScope === 'following' && this.state.series && this.state.series.status === 'active')) {
      this.saveEdit(null);
      return;
    }
    const bookingId = event.bookingId || bookingDetail._id;
    const fromDate = moment(bookingDetail.bookingDate || event.date).format('YYYY-MM-DD');
    const editDate = moment(this.state.editDate).format('YYYY-MM-DD');
    const start = this.state.editStartMinutes;
    const end = this.getEditEndMinutes();
    const staffId = String(this.state.editStaffId || bookingDetail.hairdresserId || '');
    const oldStaffId = String(bookingDetail.hairdresserId || event.staffId || staffId);
    const staff = (this.props.staffList || []).find(x => String(x.id) === staffId);
    const staffName = (staff && (staff.name || staff.hairdresserName || '').split(' ')[0]) || 'They';
    this.setState({ editSaving: true, followChecking: true });
    loadSpan(moment(fromDate).add(1, 'day'), moment(fromDate).add(13, 'months'), [staffId, oldStaffId])
      .then((span) => {
        const visits = laterVisitsOf(span, bookingDetail.seriesId, bookingId, fromDate);
        const dates = laterClashes(span, visits, bookingDetail.seriesId, { staffId, staffName, start, end, dayShift: moment(editDate).diff(moment(fromDate), 'days') });
        if (needsCheck({ dates })) {
          setCheckingDates(true);
          this.setState({ editSaving: false, followChecking: false, followCheck: { dates, visits: visits.length }, followChoices: {}, followTimes: {} });
          return;
        }
        this.setState({ followChecking: false });
        this.saveEdit(null);
      })
      .catch(() => {
        // The diary couldn't be read: say so, and let them decide
        this.setState({ editSaving: false, followChecking: false });
        toast.error('Couldn’t check the later visits for clashes. Try again, or change just this visit.');
      });
  }

  closeFollowCheck = () => {
    setCheckingDates(false);
    cancelPlacement({ quiet: true });
    this.setState({ followCheck: null, followChoices: {}, followTimes: {}, tucked: false });
  }

  // Change time for one later visit: the panel tucks away and the visit sits
  // on its day in the diary to drag, as with a new repeat
  placeLaterVisit = (d) => {
    const { followCheck, followTimes, editStartMinutes } = this.state;
    const list = ((followCheck && followCheck.dates) || []).filter(x => x.status !== 'ok');
    const i = list.findIndex(x => x.date === d.date);
    const next = i >= 0 ? list[i + 1] : null;
    const staffId = String(this.state.editStaffId || (this.state.bookingDetail && this.state.bookingDetail.hairdresserId) || '');
    const detail = this.state.bookingDetail || {};
    try { window.dispatchEvent(new CustomEvent('beu:diary-go-to-date', { detail: { date: d.date, staffId } })); } catch (e) { /* ignore */ }
    startPlacement({
      date: d.date,
      staffId,
      minutes: followTimes[d.date] != null ? followTimes[d.date] : editStartMinutes,
      original: editStartMinutes,
      duration: this.getEditEndMinutes() - editStartMinutes,
      label: (detail.user && detail.user.name) || '',
      step: { index: i + 1, total: list.length, hasNext: !!next },
    }, {
      onDone: (minutes, t, { next: goOn } = {}) => {
        if (minutes !== editStartMinutes) {
          this.setState(st => ({ followTimes: { ...st.followTimes, [d.date]: minutes }, followChoices: { ...st.followChoices, [d.date]: 'custom' } }));
        }
        if (goOn && next) { setTimeout(() => this.placeLaterVisit(next), 0); return; }
        this.setState({ tucked: false });
      },
      onCancel: () => this.setState({ tucked: false }),
    });
    this.setState({ tucked: true });
  }

  renderFollowCheck() {
    const { followCheck, followChoices, followTimes, editSaving, editStartMinutes } = this.state;
    const dates = followCheck.dates;
    const n = 1 + bookedCount(dates, followChoices, followTimes);
    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={this.closeFollowCheck} aria-label="Back to the booking">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="bdp-header-info"><h3 className="bdp-client-name">Check the later visits</h3></div>
          </div>
        </div>
        <div className="bdp-body">
          <div className="rpt-check-wrap">
            <p className="rpt-check-note">Moving {followCheck.visits} later visit{followCheck.visits === 1 ? '' : 's'} to match. These clash at the new time.</p>
            <DateCheck
              dates={dates}
              choices={followChoices}
              times={followTimes}
              startMinutes={editStartMinutes}
              timeLabel={formatTime(editStartMinutes)}
              words={{ keep: 'Move anyway', skip: 'Leave as it was', bulkKeep: 'Move all anyway', bulkSkip: 'Leave all as they were' }}
              onChoose={(date, how) => this.setState(st => ({ followChoices: { ...st.followChoices, [date]: how } }))}
              onChooseAll={(status, how) => this.setState((st) => {
                const next = { ...st.followChoices };
                dates.filter(d => d.status === status).forEach((d) => { next[d.date] = how; });
                return { followChoices: next };
              })}
              onChangeTime={this.placeLaterVisit}
              onView={this.placeLaterVisit}
            />
          </div>
        </div>
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={this.closeFollowCheck} disabled={editSaving}>Back</button>
          <button className="bdp-btn bdp-btn--primary" onClick={() => this.saveEdit(this.state.followCheck)} disabled={editSaving}>
            {editSaving ? 'Saving...' : `Save ${n} visit${n === 1 ? '' : 's'}`}
          </button>
        </div>
      </>
    );
  }

  saveEdit = (followFix) => {
    const { event, dispatch, onRefresh } = this.props;
    const { editStartMinutes, editDate, bookingDetail, editServices } = this.state;

    if (editServices.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    const editEndMinutes = this.getEditEndMinutes();
    if (editEndMinutes <= editStartMinutes) {
      toast.error('Booking must have at least one service with duration');
      return;
    }

    const detail = bookingDetail || {};
    const services = editServices;
    const hasProcessingTime = services.some(s => s.processingTime > 0);
    const params = {
      bookingID: event.bookingId || detail._id,
      bookingSlot: [editStartMinutes, editEndMinutes],
      hairdresserId: this.state.editStaffId,
      bookingDate: moment(editDate).format('YYYY-MM-DD'),
      ...(this.state.editClientId ? { clientId: this.state.editClientId } : {}),
      isProcessingTime: hasProcessingTime ? 0 : 1,
      serviceNum: 1,
      servicesToBook: services.map((o, i) => {
        const serviceRef = o.serviceId
          || o.service?.serviceId
          || o.service?._id
          || o.id;
        const isFirst = i === 0;
        const isLast = i === services.length - 1;
        return {
          service: serviceRef,
          serviceName: o.serviceName || o.service?.serviceName || o.name || '',
          serviceDescription: o.serviceDescription || o.service?.serviceDescription || '',
          servicePrice: parseFloat(o.servicePrice || o.service?.servicePrice || 0),
          serviceOriginalPrice: parseFloat(o.serviceOriginalPrice || o.servicePrice || o.service?.servicePrice || 0),
          serviceTime: parseInt(o.serviceTime || o.service?.serviceTime || 30, 10),
          depositPrice: parseFloat(o.depositPrice || o.service?.depositPrice || 0),
          processingTime: parseInt(o.processingTime || 0, 10),
          finishingTime: parseInt(o.finishingTime || 0, 10),
          // Multi-service rule: only the first service's setup and the last
          // service's cleanup are honoured — zero out the others on the wire.
          setupTime: isFirst ? (parseInt(o.setupTime || 0, 10)) : 0,
          cleanupTime: isLast ? (parseInt(o.cleanupTime || 0, 10)) : 0,
          diaryColour: o.diaryColour || o.service?.diaryColour || null,
          hairdresserId: o.hairdresserId || this.state.editStaffId,
        };
      }),
    };

    this.setState({ editSaving: true });
    dispatch(bookingActions.editDairyBooking(params))
      .then(response => {
        if (response?.data?.errorCode) {
          toast.error(response.data.messages || 'Could not save changes');
          this.setState({ editSaving: false });
        } else {
          toast.success('Booking updated');
          // "This and all after it": the later visits of the repeat change to match
          if (bookingDetail && bookingDetail.seriesId && this.state.editScope === 'following') {
            const bid = event.bookingId || bookingDetail._id;
            const fixState = { choices: this.state.followChoices || {}, times: this.state.followTimes || {} };
            bookingSeriesService.updateFollowing(bid)
              .then((r) => this.applyLaterChoices(followFix, fixState, params).then((fixed) => {
                const bad = (r.couldNotBook || []).length;
                const parts = [
                  fixed.left ? `${fixed.left} left as ${fixed.left === 1 ? 'it was' : 'they were'}` : null,
                  fixed.moved ? `${fixed.moved} at ${fixed.moved === 1 ? 'its own time' : 'their own times'}` : null,
                  bad ? `${bad} couldn’t be booked, see the booking` : null,
                  fixed.failed ? `${fixed.failed} couldn’t be changed, change ${fixed.failed === 1 ? 'it' : 'them'} one by one` : null,
                ].filter(Boolean);
                toast.success(`Updated ${r.changed} later visit${r.changed === 1 ? '' : 's'} to match${parts.length ? `. ${parts.join(', ')}` : ''}`);
                if (onRefresh) onRefresh();
              }))
              .catch((e) => toast.error(e.message || 'Could not update the later visits'));
          }
          if (event && event._dropOverrides) markFirstTask('move'); // dragged, then confirmed
          // Send notification email if requested
          const bookingId = event.bookingId || bookingDetail?._id;
          if (this.state.notifyEmail && bookingId) {
            bookingService.resendBookingConfirmation(bookingId)
              .then((res) => {
                const body = res && res.data;
                if (body && body.statusCode === 1) toast.success((body.responseData && body.responseData.message) || 'Update emailed to the client');
                else toast.error((body && body.error && body.error.responseMessage) || 'Could not email the client');
              })
              .catch(() => toast.error('Could not email the client'));
          }
          setCheckingDates(false);
          this.setState({ editSaving: false, editMode: false, notifyEmail: false, followCheck: null, tucked: false });
          if (onRefresh) onRefresh();
          if (this.props.onClose) this.props.onClose();
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
        this.setState({ editSaving: false });
      });
  }

  // After the later visits moved to match: the ones they chose to leave go
  // back to their old time (and stylist), the ones they gave their own time
  // go there. One at a time, so a refusal names itself.
  applyLaterChoices = async (followFix, { choices, times }, params) => {
    const out = { left: 0, moved: 0, failed: 0 };
    if (!followFix) return out;
    const duration = params.bookingSlot[1] - params.bookingSlot[0];
    const withStaff = (list, staffId) => list.map(sv => ({ ...sv, hairdresserId: staffId }));
    for (const d of followFix.dates) {
      if (d.status === 'ok') continue;
      const c = choiceFor(d, choices, times);
      let p = null;
      if (c === 'skip') p = { ...params, bookingID: d.visitId, bookingDate: d.fromDate, bookingSlot: d.oldSlot, hairdresserId: d.oldStaffId, servicesToBook: withStaff(params.servicesToBook, d.oldStaffId) };
      else if (c === 'custom' && times[d.date] != null) p = { ...params, bookingID: d.visitId, bookingDate: d.date, bookingSlot: [times[d.date], times[d.date] + duration] };
      if (!p) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await this.props.dispatch(bookingActions.editDairyBooking(p));
        if (res && res.data && res.data.errorCode) out.failed += 1;
        else if (c === 'skip') out.left += 1;
        else out.moved += 1;
      } catch (e) { out.failed += 1; }
    }
    return out;
  }

  handleRebook = () => {
    const { event, onRebook, onClose } = this.props;
    const { bookingDetail } = this.state;
    const detail = bookingDetail || {};
    const services = (detail.servicesBooked || event.services || []).map(s => ({
      service: s.service || { serviceId: s.serviceId, _id: s.serviceId, serviceName: s.serviceName, servicePrice: s.servicePrice, diaryColour: s.diaryColour },
      serviceId: s.service?.serviceId || s.service?._id || s.serviceId,
      serviceName: s.service?.serviceName || s.serviceName || '',
      servicePrice: s.servicePrice || s.service?.servicePrice || 0,
      serviceTime: s.serviceTime || s.service?.serviceTime || 30,
      processingTime: s.processingTime || 0,
      finishingTime: s.finishingTime || 0,
      diaryColour: s.diaryColour || s.service?.diaryColour || null,
      hairdresserId: event.staffId,
    }));
    const client = detail.user || (event.clientId ? { _id: event.clientId, name: event.clientName || '' } : null);
    if (onRebook) {
      onRebook({ staffId: event.staffId, services, client });
    }
    if (onClose) onClose();
  }

  handleMenuToggle = () => {
    this.setState(prev => ({ menuOpen: !prev.menuOpen }));
  }

  // Who may refund this booking's deposit / charge its no-show fee — the same
  // rules the server enforces (an SE's money is theirs; else refunds / pos).
  _moneyActor = () => {
    const detail = this.state.bookingDetail || {};
    return { hairdresserId: detail.hairdresserId || this.props.event?.staffId, sellerIsSelfEmployed: !!detail.sellerIsSelfEmployed };
  }

  // Repeat bookings: "Repeats every 6 weeks" and stopping it
  _loadSeries = (seriesId) => {
    if (!seriesId) return;
    bookingSeriesService.get(seriesId)
      .then((series) => { if (this.state.bookingDetail && String(this.state.bookingDetail.seriesId) === String(seriesId)) this.setState({ series }); })
      .catch(() => {});
  }

  // Stop repeating: ask first (what happens to the visits already booked),
  // then show it worked, in the booking and the diary
  handleStopRepeating = () => {
    this.setState({ menuOpen: false, activeOverlay: 'stopRepeat', overlayClosing: false });
  }

  executeStopRepeating = () => {
    const bookingId = this.props.event?.bookingId;
    if (!bookingId) {
      toast.error('Couldn’t find this booking. Close it and open it again.');
      return;
    }
    this.setState({ actionLoading: true });
    bookingSeriesService.stop(bookingId, 'none')
      .then(() => {
        const left = this.state.series && this.state.series.upcoming;
        toast.success(left > 0 ? `Stopped repeating. ${left} visit${left === 1 ? '' : 's'} stay in the diary.` : 'Stopped repeating. Visits already booked stay in the diary.');
        this.setState({ actionLoading: false, activeOverlay: null });
        this._loadSeries(this.state.bookingDetail && this.state.bookingDetail.seriesId);
        if (this.props.onRefresh) this.props.onRefresh();
      })
      .catch((e) => { toast.error(e.message || 'Couldn’t stop the repeat. Try again.'); this.setState({ actionLoading: false }); });
  }

  // From the stop sheet: cancel the upcoming visits instead (the cancel
  // sheet, so the deposit choice and the client email work as they do there)
  stopAndCancelInstead = () => {
    this.setState({ activeOverlay: 'cancel', cancelScope: 'following', overlayClosing: false, refundDepositChecked: canRefundDeposit(this._moneyActor()) });
  }

  // ── Pause: skip the visits between two dates, keep the repeat ──
  // This visit's date, as the repeat lists it (the salon's day)
  _visitDate = () => {
    const id = String(this.props.event?.bookingId || '');
    const v = this.state.series && Array.isArray(this.state.series.visits) ? this.state.series.visits.find(x => String(x.bookingId) === id) : null;
    const detail = this.state.bookingDetail || {};
    return (v && v.date) || moment(detail.bookingDate || this.props.event?.date).format('YYYY-MM-DD');
  }

  handlePauseRepeating = () => {
    const from = this._visitDate();
    this.setState({ menuOpen: false, activeOverlay: 'pauseRepeat', overlayClosing: false, pauseFrom: from, pauseTo: from, pauseOpen: null });
  }

  executePause = () => {
    const bookingId = this.props.event?.bookingId;
    const { pauseFrom, pauseTo } = this.state;
    const problem = pauseProblem(pauseFrom, pauseTo);
    if (problem) { toast.error(problem); return; }
    this.setState({ actionLoading: true });
    bookingSeriesService.pause(bookingId, pauseFrom, pauseTo)
      .then((res) => {
        const n = Number(res && res.paused) || 0;
        const to = moment((res && res.to) || pauseTo).format('D MMM');
        toast.success(n > 0
          ? `Paused. ${n} visit${n === 1 ? '' : 's'} skipped, the client gets one message with the dates. The repeat carries on after ${to}.`
          : `Paused to ${to}. No booked visits fell in those dates.`);
        if (res && res.failed && res.failed.length) toast.error(`${res.failed.length} couldn’t be skipped. Cancel them one by one.`);
        this.setState({ actionLoading: false, activeOverlay: null });
        if (this.props.onRefresh) this.props.onRefresh();
        // This visit was one of them: it's cancelled now, so the panel closes
        if (pauseFrom <= this._visitDate() && this._visitDate() <= pauseTo && n > 0) {
          if (this.props.onClose) this.props.onClose();
          return;
        }
        this._loadSeries(this.state.bookingDetail && this.state.bookingDetail.seriesId);
      })
      .catch((e) => { toast.error(e.message || 'Couldn’t pause the repeat. Try again.'); this.setState({ actionLoading: false }); });
  }

  renderPauseOverlay() {
    const { actionLoading, overlayClosing, series, pauseFrom, pauseTo, pauseOpen } = this.state;
    const problem = pauseProblem(pauseFrom, pauseTo);
    const words = problem ? null : pauseWords(visitsToPause(series, pauseFrom, pauseTo), pauseFrom, pauseTo);
    const field = (key, label, value) => (
      <div className="bdp-edit-field">
        <label className="bdp-edit-label">{label}</label>
        <div
          className="bdp-edit-input"
          role="button"
          tabIndex={0}
          ref={(el) => { this[`_pause_${key}`] = el; }}
          onClick={() => this.setState({ pauseOpen: key })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.setState({ pauseOpen: key }); }}
        >
          {value ? moment(value).format('ddd D MMM YYYY') : 'Choose a date'}
        </div>
        <DiaryDatePicker
          open={pauseOpen === key}
          selected={value ? moment(value).toDate() : new Date()}
          anchorEl={this[`_pause_${key}`]}
          onChange={(d) => {
            const v = moment(d).format('YYYY-MM-DD');
            // The last date follows the first when it would end up before it
            this.setState(key === 'from' ? { pauseFrom: v, pauseTo: pauseTo && pauseTo >= v ? pauseTo : v, pauseOpen: null } : { pauseTo: v, pauseOpen: null });
          }}
          onClose={() => this.setState({ pauseOpen: null })}
        />
      </div>
    );
    return (
      <div className={`cancel-booking-overlay bdp-overlay${overlayClosing ? ' closing' : ''}`} onClick={this.handleCloseOverlay}>
        <div className={`cancel-booking-sheet${overlayClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()} role="dialog" aria-label="Pause the repeat">
          <div className="cancel-booking-header">
            <div className="cancel-booking-header-spacer" />
            <h2 className="cancel-booking-header-title">Pause the repeat</h2>
            <button className="cancel-booking-nav-btn" onClick={this.handleCloseOverlay} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
          </div>
          <div className="cancel-booking-body">
            <p className="cancel-booking-text">
              Skip the visits between two dates, both included. The repeat keeps going after. Your client gets one message listing the dates.
            </p>
            <div className="rpt-pause">
              <div className="rpt-pause-dates">
                {field('from', 'First date', pauseFrom)}
                {field('to', 'Last date', pauseTo)}
              </div>
              <div className="rpt-picks" role="group" aria-label="Quick last dates">
                {pauseFrom && PAUSE_PICKS.map((p) => {
                  const d = pauseUntil(pauseFrom, p);
                  return <button key={p.label} type="button" className={`rpt-chip${pauseTo === d ? ' is-on' : ''}`} aria-pressed={pauseTo === d} onClick={() => this.setState({ pauseTo: d })}>{p.label}</button>;
                })}
              </div>
              <p className={`rpt-said${problem ? ' is-bad' : ''}`} aria-live="polite">{problem || words.line}</p>
            </div>
            <div className="cancel-booking-actions">
              <button className="cancel-booking-btn noshow-btn-confirm" onClick={this.executePause} disabled={actionLoading || !!problem || (words && words.none)}>
                {actionLoading ? 'Pausing...' : words ? words.button : 'Pause'}
              </button>
              <button className="cancel-booking-btn cancel-booking-btn-secondary" onClick={this.handleCloseOverlay} disabled={actionLoading}>Keep every visit</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Undo: a repeat this person booked in the last 10 minutes ──
  handleUndoRepeat = () => {
    const seriesId = this.state.bookingDetail && this.state.bookingDetail.seriesId;
    if (!seriesId || this.state.undoing) return;
    this.setState({ undoing: true });
    undoRepeat(seriesId)
      .then(() => {
        if (this._unmounted) return;
        this.setState({ undoing: false });
        if (this.props.onRefresh) this.props.onRefresh();
        if (this.props.onClose) this.props.onClose();
      })
      .catch(() => { if (!this._unmounted) this.setState({ undoing: false }); });
  }

  // ── Change how often: from this visit on ──
  handleChangeRule = () => {
    const { series } = this.state;
    const repeat = repeatFromSeries(series, visitsAround(series, this.props.event?.bookingId));
    this.setState({ menuOpen: false, ruleEdit: { repeat, before: repeat, check: null, choices: {}, times: {}, busy: false } });
  }

  closeRuleEdit = () => this.setState({ ruleEdit: null })

  _ruleDuration = () => {
    const detail = this.state.bookingDetail || {};
    const slot = Array.isArray(detail.bookingSlot) ? detail.bookingSlot : null;
    if (slot && slot.length === 2) return Number(slot[1]) - Number(slot[0]);
    const ev = this.props.event || {};
    return Math.max(0, (ev.endMinutes || 0) - (ev.startMinutes || 0));
  }

  _ruleStart = () => {
    const detail = this.state.bookingDetail || {};
    return Array.isArray(detail.bookingSlot) ? Number(detail.bookingSlot[0]) : (this.props.event?.startMinutes || 0);
  }

  // Check the dates first, as a new repeat does. All free: change it
  // straight away; otherwise Check the dates opens with the choices.
  checkRuleChange = () => {
    const r = this.state.ruleEdit;
    const bookingId = this.props.event?.bookingId;
    const firstDate = this._visitDate();
    if (!r || !ruleFor(r.repeat.freq)) return;
    if (!endIsValid({ ...r.repeat, firstDate })) {
      toast.error(r.repeat.end === 'date' ? 'Choose a last date after this visit' : 'Choose 2 visits or more');
      return;
    }
    const body = changeRuleBody({ bookingId, repeat: r.repeat, firstDate, dates: [] });
    this.setState({ ruleEdit: { ...r, busy: true } });
    bookingSeriesService.changeRuleCheck({ bookingId, rule: body.rule, until: body.until })
      .then((res) => {
        const dates = withoutOwnVisits(res && res.dates, ownVisitIds(this.state.series, bookingId), firstDate);
        const check = { ...res, dates };
        if (needsCheck({ dates })) {
          setCheckingDates(true);
          this.setState(st => ({ ruleEdit: { ...st.ruleEdit, busy: false, check, choices: {}, times: {} } }));
          return;
        }
        this.setState(st => ({ ruleEdit: { ...st.ruleEdit, check } }), this.saveRuleChange);
      })
      .catch((e) => {
        this.setState(st => ({ ruleEdit: st.ruleEdit && { ...st.ruleEdit, busy: false } }));
        toast.error(e.message || 'Couldn’t check the dates. Try again.');
      });
  }

  saveRuleChange = () => {
    const r = this.state.ruleEdit;
    if (!r) return;
    const bookingId = this.props.event?.bookingId;
    const firstDate = this._visitDate();
    const body = changeRuleBody({ bookingId, repeat: r.repeat, firstDate, dates: (r.check && r.check.dates) || [], choices: r.choices, times: r.times, duration: this._ruleDuration() });
    this.setState({ ruleEdit: { ...r, busy: true } });
    bookingSeriesService.changeRule(body)
      .then((res) => {
        setCheckingDates(false);
        const n = Number(res && res.changed) || 0;
        const missed = (res && res.couldNotBook) || [];
        toast.success(`${repeatSaid({ ...r.repeat, firstDate })} from ${dateSaid(firstDate)}. ${n} later visit${n === 1 ? '' : 's'} booked. Your client gets one email with the new dates.`);
        if (missed.length) toast.error(`${missed.length} date${missed.length === 1 ? '' : 's'} couldn’t be booked. They’re listed on the repeat.`);
        this.setState({ ruleEdit: null });
        if (this.props.onRefresh) this.props.onRefresh();
        // This visit now belongs to the changed repeat
        this.fetchDetail();
      })
      .catch((e) => {
        this.setState(st => ({ ruleEdit: st.ruleEdit && { ...st.ruleEdit, busy: false } }));
        toast.error(e.message || 'Couldn’t change the repeat. Try again.');
      });
  }

  renderRuleEdit() {
    const r = this.state.ruleEdit;
    const firstDate = this._visitDate();
    const start = this._ruleStart();
    const back = r.check
      ? () => { setCheckingDates(false); this.setState({ ruleEdit: { ...r, check: null, choices: {}, times: {}, busy: false } }); }
      : this.closeRuleEdit;
    const header = (title) => (
      <div className="bdp-header">
        <div className="bdp-header-left">
          <button className="bdp-back-btn" onClick={back} aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="bdp-header-info"><h3 className="bdp-client-name">{title}</h3></div>
        </div>
      </div>
    );
    if (r.check) {
      const dates = r.check.dates || [];
      // This visit stays; the button counts the later visits booked
      const later = dates.filter(d => d.date !== firstDate);
      const n = bookedCount(later, r.choices, r.times);
      return (
        <>
          {header('Check the dates')}
          <div className="bdp-body">
            <div className="rpt-check-wrap">
              <p className="rpt-check-note">{repeatSaid({ ...r.repeat, firstDate })}, from {dateSaid(firstDate)}. These dates need a look first.</p>
              <DateCheck
                dates={later}
                choices={r.choices}
                times={r.times}
                startMinutes={start}
                timeLabel={formatTime(start)}
                onTime={(date, minutes) => this.setState(st => ({ ruleEdit: { ...st.ruleEdit, times: { ...st.ruleEdit.times, [date]: minutes }, choices: { ...st.ruleEdit.choices, [date]: 'custom' } } }))}
                onChoose={(date, how) => this.setState(st => ({ ruleEdit: { ...st.ruleEdit, choices: { ...st.ruleEdit.choices, [date]: how } } }))}
                onChooseAll={(status, how) => this.setState((st) => {
                  const next = { ...st.ruleEdit.choices };
                  later.filter(d => d.status === status).forEach((d) => { next[d.date] = how === 'move' && !d.suggestion ? (d.status === 'overlap' ? 'keep' : 'skip') : how; });
                  return { ruleEdit: { ...st.ruleEdit, choices: next } };
                })}
              />
            </div>
          </div>
          <div className="bdp-footer">
            <button className="bdp-btn bdp-btn--secondary" onClick={back} disabled={r.busy}>Back</button>
            <button className="bdp-btn bdp-btn--primary" onClick={this.saveRuleChange} disabled={r.busy}>
              {r.busy ? 'Saving...' : `Book ${n} later visit${n === 1 ? '' : 's'}`}
            </button>
          </div>
        </>
      );
    }
    const off = !ruleFor(r.repeat.freq);
    const unchanged = sameRepeat(r.repeat, r.before);
    return (
      <>
        {header('Change how often')}
        <div className="bdp-body">
          <div className="rpt-from" role="note">
            <b>From this visit on ({dateSaid(firstDate)})</b>
            <span>Visits before it stay as they are. This visit keeps its time. The later visits are rebooked on the new pattern at {formatTime(start)}, and your client gets one email with the new dates.</span>
          </div>
          <div className="rpt-rule-wrap">
            <RepeatControl value={r.repeat} firstDate={firstDate} onChange={repeat => this.setState(st => ({ ruleEdit: { ...st.ruleEdit, repeat } }))} />
            {off && <p className="rpt-said is-bad">To end it, use Stop repeating instead.</p>}
          </div>
        </div>
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={this.closeRuleEdit} disabled={r.busy}>Cancel</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.checkRuleChange} disabled={r.busy || off || unchanged}>
            {r.busy ? 'Checking the dates...' : `Change from ${dateSaid(firstDate)}`}
          </button>
        </div>
      </>
    );
  }

  renderStopRepeatOverlay() {
    const { actionLoading, overlayClosing, series } = this.state;
    const left = series && series.upcoming;
    return (
      <div className={`cancel-booking-overlay bdp-overlay${overlayClosing ? ' closing' : ''}`} onClick={this.handleCloseOverlay}>
        <div className={`cancel-booking-sheet${overlayClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()} role="dialog" aria-label="Stop repeating">
          <div className="cancel-booking-header">
            <div className="cancel-booking-header-spacer" />
            <h2 className="cancel-booking-header-title">Stop repeating?</h2>
            <button className="cancel-booking-nav-btn" onClick={this.handleCloseOverlay} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
          </div>
          <div className="cancel-booking-body">
            <div className="cancel-booking-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#E3F1F5"/>
                <path d="M19 28a9 9 0 0 1 15.3-6.3L37 24.5M37 18.5v6h-6" stroke="#007EA0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 35L35 21" stroke="#007EA0" strokeWidth="2.4" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="cancel-booking-text">
              No more visits will be added. {left > 0 ? `The ${left} visit${left === 1 ? '' : 's'} already booked stay in the diary.` : 'Visits already booked stay in the diary.'} Your client isn’t sent anything.
            </p>
            <div className="cancel-booking-actions">
              <button className="cancel-booking-btn noshow-btn-confirm" onClick={this.executeStopRepeating} disabled={actionLoading}>
                {actionLoading ? 'Stopping...' : 'Stop repeating'}
              </button>
              <button className="cancel-booking-btn cancel-booking-btn-secondary" onClick={this.handleCloseOverlay} disabled={actionLoading}>Keep repeating</button>
            </div>
            <button type="button" className="rpt-stop-alt" onClick={this.stopAndCancelInstead} disabled={actionLoading}>Cancel the upcoming visits instead</button>
          </div>
        </div>
      </div>
    );
  }

  handleCancelBooking = () => {
    // Refund defaults ON when a paid deposit exists AND this person may
    // refund it — the salon is the one pulling the appointment, so the client
    // getting their money back is the expected outcome; unticking keeps the
    // deposit. Someone who can't refund never gets it pre-ticked.
    this.setState({ activeOverlay: 'cancel', menuOpen: false, refundDepositChecked: canRefundDeposit(this._moneyActor()) });
  }

  handleNoShow = () => {
    const { bookingDetail } = this.state;
    const detail = bookingDetail || {};
    const hasCard = detail.stripePaymentMethodId != null && detail.stripePaymentMethodId !== '';
    const noShowPct = detail.noShowFeeInPercentage || 0;

    // Calculate payment amounts same as ViewBooking
    let totalAmount = 0;
    (detail.servicesBooked || []).forEach(s => { totalAmount += parseFloat(s.servicePrice) || 0; });
    let totalPaid = 0;
    (detail.transaction || []).forEach(t => { if (t.paymentType !== 5) totalPaid += parseFloat(t.amount) || 0; });
    totalPaid += paidFromTransactions(detail.transactions);
    const pendingAmount = totalAmount - totalPaid;

    let noShowFeeAmount = '0.00';
    let noShowSelectedOption = 2;
    if (noShowPct > 0) {
      noShowFeeAmount = ((pendingAmount * noShowPct) / 100).toFixed(2);
      if (parseFloat(noShowFeeAmount) > 0 && hasCard && canChargeNoShow(this._moneyActor())) {
        noShowSelectedOption = 1;
      }
    }

    this.setState({
      activeOverlay: 'noshow',
      menuOpen: false,
      noShowSelectedOption,
      noShowFeeAmount,
      noShowErrorStatus: false,
    });
  }

  handleCloseOverlay = () => {
    this.setState({ overlayClosing: true });
    setTimeout(() => {
      this.setState({ activeOverlay: null, overlayClosing: false, actionLoading: false });
    }, 300);
  }

  executeCancelBooking = () => {
    const { bookingDetail } = this.state;
    const { event, dispatch } = this.props;
    const hairdresserId = bookingDetail?.hairdresserId || localStorage.getItem('userId');
    const bookingId = event?.bookingId;
    if (!bookingId || !hairdresserId) return;

    // Cancelling this and the rest of a repeat: one call, one message to the client
    if (bookingDetail && bookingDetail.seriesId && this.state.cancelScope && this.state.cancelScope !== 'this'
      && cancelScopes(visitsAround(this.state.series, bookingId)).includes(this.state.cancelScope)) {
      this.setState({ actionLoading: true });
      bookingSeriesService.stop(bookingId, this.state.cancelScope, this.state.refundDepositChecked && canRefundDeposit(this._moneyActor()) ? 1 : 0)
        .then((res) => {
          const n = res.cancelled || 0;
          toast.success(`Cancelled ${n} visit${n === 1 ? '' : 's'} and stopped repeating`);
          if (res.failed && res.failed.length) toast.error(`${res.failed.length} couldn’t be cancelled. Cancel them one by one.`);
          this.setState({ actionLoading: false, activeOverlay: null });
          if (this.props.onRefresh) this.props.onRefresh();
          if (this.props.onClose) this.props.onClose();
        })
        .catch((e) => { toast.error(e.message || 'Something went wrong'); this.setState({ actionLoading: false }); });
      return;
    }

    this.setState({ actionLoading: true });
    dispatch(bookingActions.hairdresserCancelBooking({
      hairdresserId,
      bookingID: bookingId,
      refundDeposit: this.state.refundDepositChecked && canRefundDeposit(this._moneyActor()) ? 1 : 0,
      deviceToken: 'dfdsfdsfsd',
    }))
      .then(response => {
        if (response?.data?.errorCode) {
          toast.error(response.data.messages || 'Could not cancel booking');
          this.setState({ actionLoading: false });
        } else {
          const data = response?.data?.responseData || {};
          const restored = parseFloat(data.voucherRestored) || 0;
          const refunded = parseFloat(data.depositRefunded) || 0;
          const currency = this.state.bookingDetail?.currencySymbol || this.props.currencySymbol || '£';
          const parts = [];
          if (refunded > 0) parts.push(`${currency}${refunded.toFixed(2)} deposit refunded`);
          if (restored > 0) parts.push(`${currency}${restored.toFixed(2)} gift card returned`);
          toast.success(parts.length ? `Booking cancelled — ${parts.join(', ')}` : 'Booking cancelled');
          if (data.depositRefundErrors && data.depositRefundErrors.length) {
            toast.error('Deposit refund hit a problem — check the admin Reconciliations feed.');
          }
          this.setState({ actionLoading: false, activeOverlay: null });
          if (this.props.onRefresh) this.props.onRefresh();
          if (this.props.onClose) this.props.onClose();
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
        this.setState({ actionLoading: false });
      });
  }

  executeNoShowBooking = () => {
    const { noShowFeeAmount, noShowSelectedOption, noShowErrorStatus } = this.state;
    const { event, dispatch } = this.props;
    const bookingId = event?.bookingId;
    if (!bookingId || noShowErrorStatus) return;
    if (+noShowSelectedOption === 1 && !canChargeNoShow(this._moneyActor())) {
      toast.error("You don't have permission to charge this booking's card");
      return;
    }

    const params = {
      bookingId,
      deviceToken: 'dfdsfdsfsd',
      noShowAmount: +noShowFeeAmount,
      noShowChargeStatus: +noShowSelectedOption,
    };

    if (params.noShowAmount <= 0 && params.noShowChargeStatus === 1) {
      toast.error('Invalid Amount');
      return;
    }
    if (params.noShowChargeStatus === 2) {
      params.noShowAmount = 0;
    }

    this.setState({ actionLoading: true });
    dispatch(bookingActions.noShowBooking(params))
      .then(response => {
        if (response?.data?.statusCode === 0) {
          toast.error(response?.data?.error?.responseMessage || 'Please try again later');
          this.setState({ actionLoading: false });
        } else if (response?.data?.statusCode === 1) {
          toast.success(response?.data?.responseData?.message || 'Marked as no show');
          this.setState({ actionLoading: false, activeOverlay: null });
          if (this.props.onRefresh) this.props.onRefresh();
          if (this.props.onClose) this.props.onClose();
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
        this.setState({ actionLoading: false });
      });
  }

  handleNoShowOptionChange = (option) => {
    this.setState({ noShowSelectedOption: option });
  }

  handleNoShowAmountChange = (e) => {
    const { bookingDetail } = this.state;
    const noShowPct = bookingDetail?.noShowFeeInPercentage || 0;
    let totalAmount = 0;
    (bookingDetail?.servicesBooked || []).forEach(s => { totalAmount += parseFloat(s.servicePrice) || 0; });
    let totalPaid = 0;
    (bookingDetail?.transaction || []).forEach(t => { if (t.paymentType !== 5) totalPaid += parseFloat(t.amount) || 0; });
    totalPaid += paidFromTransactions(bookingDetail?.transactions);
    const pendingAmount = totalAmount - totalPaid;
    const maxFee = (pendingAmount * noShowPct) / 100;

    const val = e.target.value;
    this.setState({
      noShowFeeAmount: val,
      noShowErrorStatus: parseFloat(val) > maxFee,
    });
  }

  renderCancelOverlay() {
    const { actionLoading, overlayClosing, refundDepositChecked } = this.state;
    const detail = this.state.bookingDetail || {};
    const currency = detail.currencySymbol || this.props.currencySymbol || '£';
    // Paid deposit total — prefer the succeeded deposit rows from the
    // transactions collection (covers multiple deposits), fall back to the
    // booking-level figure.
    let paidDeposit = 0;
    (detail.transactions || []).forEach(t => {
      if (t.pgObjectType === 'deposit' && t.pgTransactionStatus === 'succeeded') {
        paidDeposit += parseFloat(t.pgTransactionAmount) || 0;
      }
    });
    if (paidDeposit === 0 && detail.isDepositPay === 1) {
      paidDeposit = parseFloat(detail.depositAmount) || 0;
    }
    const hasGiftCard = (detail.transaction || []).some(t => t.paymentType === 11);
    const mayRefund = canRefundDeposit(this._moneyActor());
    // A repeat: which visits, said with the dates and counts when known
    const around = detail.seriesId ? visitsAround(this.state.series, this.props.event?.bookingId) : null;
    const scopes = cancelScopes(around);
    const scope = scopes.includes(this.state.cancelScope) ? this.state.cancelScope : 'this';
    const words = detail.seriesId ? cancelWords(scope, around) : cancelWords('this', null);
    return (
      <div className={`cancel-booking-overlay bdp-overlay${overlayClosing ? ' closing' : ''}`} onClick={this.handleCloseOverlay}>
        <div className={`cancel-booking-sheet${overlayClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
          <div className="cancel-booking-header">
            <div className="cancel-booking-header-spacer" />
            <h2 className="cancel-booking-header-title">Cancel Booking</h2>
            <button className="cancel-booking-nav-btn" onClick={this.handleCloseOverlay} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="cancel-booking-body">
            <div className="cancel-booking-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#FEE2E2"/>
                <path d="M20 20L36 36M20 36L36 20" stroke="#DC2626" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            {detail.seriesId && (
              <ScopeOptions
                name="bdp_cancel_scope"
                scopes={scopes}
                value={scope}
                disabled={actionLoading}
                onChange={(next) => this.setState({ cancelScope: next })}
                labelFor={cancelScopeSaid}
                detailFor={(x) => scopeDetail(x, around, detail.bookingDate || this.props.event?.date)}
              />
            )}
            <p className="cancel-booking-text">{words.text}</p>
            {paidDeposit > 0 && !mayRefund ? (
              <p className="cancel-booking-text cancel-booking-refund-note">
                {detail.sellerIsSelfEmployed
                  ? `The ${currency}${paidDeposit.toFixed(2)} deposit will be kept. Only the stylist can refund it.`
                  : `The ${currency}${paidDeposit.toFixed(2)} deposit will be kept. Ask someone who can refund sales if it should go back.`}
              </p>
            ) : paidDeposit > 0 ? (
              <div className="cancel-booking-refund">
                {/* Same option-row + custom-radio pattern as the no-show
                    sheet's charge choice, so the two sheets read as a family. */}
                <div className="transaction_option" onClick={() => !actionLoading && this.setState({ refundDepositChecked: true })}>
                  <label className="radio-container">
                    <input type="radio" name="bdp_cancel_refund" checked={!!refundDepositChecked} readOnly />
                    <span className="checkmark"></span>
                  </label>
                  <span className="cancel-booking-refund-label">Refund {currency}{paidDeposit.toFixed(2)} deposit to client</span>
                </div>
                <div className="transaction_option" onClick={() => !actionLoading && this.setState({ refundDepositChecked: false })}>
                  <label className="radio-container">
                    <input type="radio" name="bdp_cancel_refund" checked={!refundDepositChecked} readOnly />
                    <span className="checkmark"></span>
                  </label>
                  <span className="cancel-booking-refund-label">Keep the deposit</span>
                </div>
                <p className="cancel-booking-text cancel-booking-refund-note">
                  {refundDepositChecked
                    ? 'Your client gets the full amount back to their original payment method. The full deposit, including processing fees, is reclaimed from your account.'
                    : 'The deposit will be kept. You can still refund it manually later via support.'}
                </p>
              </div>
            ) : (
              <p className="cancel-booking-text">If your client paid in advance, they will not be automatically refunded.</p>
            )}
            {hasGiftCard && (
              <p className="cancel-booking-text">Any gift card used on this booking will be returned to the client's card automatically.</p>
            )}
            <div className="cancel-booking-actions">
              <button className="cancel-booking-btn cancel-booking-btn-danger" onClick={this.executeCancelBooking} disabled={actionLoading}>
                {actionLoading ? 'Cancelling...' : words.button}
              </button>
              <button className="cancel-booking-btn cancel-booking-btn-secondary" onClick={this.handleCloseOverlay} disabled={actionLoading}>Go Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderNoShowOverlay() {
    const { bookingDetail, actionLoading, overlayClosing, noShowSelectedOption, noShowFeeAmount, noShowErrorStatus } = this.state;
    const detail = bookingDetail || {};
    const hasCard = detail.stripePaymentMethodId != null && detail.stripePaymentMethodId !== '';
    const noShowPct = detail.noShowFeeInPercentage || 0;

    // Calculate amounts for display
    let totalAmount = 0;
    (detail.servicesBooked || []).forEach(s => { totalAmount += parseFloat(s.servicePrice) || 0; });
    let totalPaid = 0;
    (detail.transaction || []).forEach(t => { if (t.paymentType !== 5) totalPaid += parseFloat(t.amount) || 0; });
    totalPaid += paidFromTransactions(detail.transactions);

    const currency = detail.currencySymbol || detail.hairdresser?.hairdresser?.currencySymbol || this.props.currencySymbol || '£';
    const createdBy = detail.createdByName || detail.createdBy?.name || '';
    const bookedOnline = isOnlineBookingDetail(detail, this.props.event);
    const clientFirst = String((detail.user && (detail.user.first_name || detail.user.name)) || '').trim().split(/\s+/)[0];
    const createdTime = detail.createdTime ? moment(detail.createdTime).format('h:mmA, DD MMMM YYYY') : '';

    const mayCharge = canChargeNoShow(this._moneyActor());
    const disabledClass = (!hasCard || !mayCharge || (noShowSelectedOption !== 1)) ? ' disabled' : '';

    return (
      <div className={`cancel-booking-overlay bdp-overlay${overlayClosing ? ' closing' : ''}`} onClick={this.handleCloseOverlay}>
        <div className={`cancel-booking-sheet noshow-sheet${overlayClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
          <div className="cancel-booking-header">
            <div className="cancel-booking-header-spacer" />
            <h2 className="cancel-booking-header-title">Client No Show</h2>
            <button className="cancel-booking-nav-btn" onClick={this.handleCloseOverlay} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="noshow-sheet-body">
            <h6>{bookedOnline
              ? <>Booked online{clientFirst ? <> by <span>{clientFirst}</span></> : null}</>
              : <>Made in the salon{createdBy ? <> by <span>{createdBy}</span></> : null}</>}
              {createdTime ? ` at ${createdTime}` : ''}.</h6>
            {hasCard ? (
              <p className="_paragrap">The client accepted that if they did not show up for their appointment then they may be charged up to {noShowPct}% of the remaining appointment value.</p>
            ) : (
              <p className="_paragrap">{noCardReason(detail)}</p>
            )}
            <h5 className={totalPaid > 0 ? 'mb-2' : ''}>Service cost - {currency}{totalAmount.toFixed(2)}</h5>
            {totalPaid > 0 && <h5>Deposit Already Paid - {currency}{totalPaid.toFixed(2)}</h5>}

            {hasCard && !mayCharge && (
              <p className="_paragrap">
                {detail.sellerIsSelfEmployed
                  ? "This is a self-employed stylist's booking. Only they (or the salon owner) can charge the client's card."
                  : "You don't have permission to charge the card on other people's bookings."}
              </p>
            )}
            <div
              className={`transaction_option${(!hasCard || !mayCharge) ? ' disabled' : ''}`}
              onClick={() => hasCard && mayCharge && this.handleNoShowOptionChange(1)}
            >
              <label className="radio-container">
                <input type="radio" name="bdp_noshow" checked={noShowSelectedOption === 1} readOnly />
                <span className="checkmark"></span>
              </label>
              <div className="editable_currncy">
                <p>Charge clients card</p>
                <div className="currency_div mt-0 ml-0">
                  <div className="text-center px-3 currency_sml">{currency}</div>
                  <input
                    type="number"
                    value={noShowFeeAmount}
                    onChange={this.handleNoShowAmountChange}
                    className="text-center pl-3 currency_sml1 currency_sml_input edit_price_input"
                    disabled={noShowSelectedOption !== 1}
                    placeholder="00.00"
                  />
                </div>
              </div>
            </div>
            {noShowErrorStatus && <p className="text-danger text-right mb-0 mr-4 noshow_error">Enter valid amount</p>}
            <div className="transaction_option" onClick={() => this.handleNoShowOptionChange(2)}>
              <label className="radio-container">
                <input type="radio" name="bdp_noshow" checked={noShowSelectedOption === 2} readOnly />
                <span className="checkmark"></span>
              </label>
              <div className="editable_currncy">
                <p>Mark no show, no charge</p>
              </div>
            </div>
            <div className="noshow-actions">
              {noShowErrorStatus ? (
                <button className="cancel-booking-btn noshow-btn-confirm disabled">Confirm</button>
              ) : actionLoading ? (
                <button className="cancel-booking-btn noshow-btn-confirm disabled">Processing...</button>
              ) : (
                <button className="cancel-booking-btn noshow-btn-confirm" onClick={this.executeNoShowBooking}>Confirm</button>
              )}
              <button className="cancel-booking-btn cancel-booking-btn-secondary" onClick={this.handleCloseOverlay}>Go Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSkeleton() {
    return (
      <div className="bdp-skeleton">
        <div className="bdp-skel-header">
          <div className="bdp-skel-avatar" />
          <div className="bdp-skel-lines">
            <div className="bdp-skel-line bdp-skel-line--lg" />
            <div className="bdp-skel-line bdp-skel-line--sm" />
          </div>
        </div>
        <div className="bdp-skel-block" />
        <div className="bdp-skel-block bdp-skel-block--short" />
      </div>
    );
  }

  renderError() {
    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <div className="bdp-avatar bdp-avatar--initials">!</div>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Something went wrong</h3>
              <span className="bdp-client-sub">{this.state.error}</span>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="bdp-body bdp-error-body">
          <p className="bdp-error-text">We couldn't load the details for this booking. It may have been deleted or there's a connection issue.</p>
          <div className="bdp-error-actions">
            <button className="bdp-error-btn bdp-error-btn--primary" onClick={this.props.onClose}>Close</button>
            <button className="bdp-error-btn bdp-error-btn--ghost" onClick={() => this.fetchDetail()}>Try again</button>
          </div>
        </div>
      </>
    );
  }

  handleOpenInDiary = () => {
    const { event, history } = this.props;
    const bookingId = event?.bookingId || this.state.bookingDetail?._id;
    const bookingDate = event?.date || this.state.bookingDetail?.bookingDate;
    if (!bookingId) return;
    const salonId = localStorage.getItem('salonId') || '';
    if (this.props.onClose) this.props.onClose();
    setTimeout(() => {
      if (history) {
        history.push(`/diary/${salonId}`);
      }
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('beu-open-booking', {
          detail: { bookingId, date: bookingDate },
        }));
      }, 450);
    }, 200);
  }

  // `listItem` is the booking row the user clicked (from the client's
  // upcoming / past list). We keep its isOnlineBooking so the stacked view can
  // label Type / Activity correctly even when the by-id detail call doesn't
  // echo the flag. Without this, the stacked view would fall back to
  // this.props.event, i.e. the ROOT booking's flag, which is the wrong booking.
  handleViewBookingInPanel = (bookingId, listItem) => {
    if (!bookingId) return;
    const { event } = this.props;
    // Push current state onto history stack (preserve client data)
    this.setState(prev => ({
      panelHistory: [...prev.panelHistory, { bookingDetail: prev.bookingDetail, event, clientData: prev.clientData, activeTab: prev.activeTab, stackedIsOnlineBooking: prev.stackedIsOnlineBooking }],
      stackedIsOnlineBooking: listItem?.isOnlineBooking,
      isLoading: true,
      activeTab: 'details',
      clientData: null,
      clientLoading: false,
      showBookingInfo: false,
      editingClientNote: false,
      editingBookingNote: false,
    }));
    bookingService.getBookingByIdV2(bookingId)
      .then(response => {
        const raw = response?.data?.responseData;
        if (!raw) {
          this.setState({ isLoading: false });
          return;
        }
        const booking = {
          ...raw,
          user: raw.clientInfo,
          userId: raw.clientInfo?._id,
          hairdresser: raw.hairdresserInfo ? { ...raw.hairdresserInfo, hairdresser: raw.hairdresserInfo.hairdresser } : null,
          hairdresserId: raw.hairdresserInfo?._id,
        };
        this.setState({ bookingDetail: booking, isLoading: false });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handlePanelBack = () => {
    this.setState(prev => {
      const stack = [...prev.panelHistory];
      const previous = stack.pop();
      if (!previous) return null;
      return {
        panelHistory: stack,
        bookingDetail: previous.bookingDetail,
        clientData: previous.clientData,
        stackedIsOnlineBooking: previous.stackedIsOnlineBooking,
        activeTab: previous.activeTab || 'client',
        pastBookingsVisible: 5,
        showBookingInfo: false,
        editingClientNote: false,
        editingBookingNote: false,
      };
    });
  }

  handleSaveClientNote = () => {
    const { bookingDetail, clientNoteValue } = this.state;
    const userId = bookingDetail?.user?._id || bookingDetail?.userId;
    if (!userId) return;

    this.setState({ noteSaving: true });
    hairdresserService.editClient({ userId, note: clientNoteValue })
      .then(() => {
        // Update local state
        this.setState(prev => ({
          noteSaving: false,
          editingClientNote: false,
          bookingDetail: {
            ...prev.bookingDetail,
            user: { ...prev.bookingDetail?.user, note: clientNoteValue },
          },
        }));
        toast.success('Client note saved');
      })
      .catch(() => {
        this.setState({ noteSaving: false });
        toast.error('Could not save note');
      });
  }

  handleSaveBookingNote = () => {
    const { bookingDetail, bookingNoteValue } = this.state;
    const bookingId = bookingDetail?._id;
    if (!bookingId) return;

    this.setState({ noteSaving: true });
    bookingService.updateBookingNotes(bookingId, bookingNoteValue)
      .then(() => {
        this.setState(prev => ({
          noteSaving: false,
          editingBookingNote: false,
          bookingDetail: { ...prev.bookingDetail, bookingNote: bookingNoteValue },
        }));
        toast.success('Booking note saved');
      })
      .catch(() => {
        this.setState({ noteSaving: false });
        toast.error('Could not save note');
      });
  }

  handleEditClientSearch = (query) => {
    this.setState({ editClientSearchQuery: query, editClientPage: 1, editClientHasMore: true });
    if (this._editClientTimeout) clearTimeout(this._editClientTimeout);
    const doFetch = (searchKey) => {
      this.setState({ editClientSearching: true });
      hairdresserService.getClientsPaginated({ searchKey, page: 1, limit: 20, sortField: 'name', sortOrder: 'asc' })
        .then(response => {
          const data = response?.data?.responseData || response?.data?.data || {};
          const clients = data.clients || data.clientList || [];
          this.setState({ editClientResults: Array.isArray(clients) ? clients : [], editClientSearching: false, editClientHasMore: (clients || []).length >= 20 });
        })
        .catch(() => { this.setState({ editClientResults: [], editClientSearching: false }); });
    };
    if (!query.trim()) { doFetch(''); } else {
      this._editClientTimeout = setTimeout(() => doFetch(query.trim()), 300);
    }
  }

  handleEditClientScroll = (e) => {
    const { editClientPage, editClientHasMore, editClientSearchQuery } = this.state;
    if (!editClientHasMore) return;
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const nextPage = editClientPage + 1;
      hairdresserService.getClientsPaginated({ searchKey: editClientSearchQuery.trim(), page: nextPage, limit: 20, sortField: 'name', sortOrder: 'asc' })
        .then(response => {
          const data = response?.data?.responseData || response?.data?.data || {};
          const clients = data.clients || data.clientList || [];
          this.setState(prev => ({
            editClientResults: [...prev.editClientResults, ...clients],
            editClientPage: nextPage,
            editClientHasMore: (clients || []).length >= 20,
          }));
        })
        .catch(() => {});
    }
  }

  handleOpenSendForm = () => {
    this.setState({ showSendForm: true, availableFormsLoading: true });
    formService.listForms({ status: 'active', formType: 'manual' })
      .then(response => {
        const data = response.data?.responseData || {};
        this.setState({ availableForms: data.result || [], availableFormsLoading: false });
      })
      .catch(() => {
        this.setState({ availableForms: [], availableFormsLoading: false });
      });
  }

  handleSendForm = (formTemplateId) => {
    const clientId = this.state.bookingDetail?.user?._id;
    if (!clientId) return;
    this.setState({ sendingFormId: formTemplateId });
    formService.sendForm({ formTemplateId, userId: clientId })
      .then(() => {
        this.setState({ showSendForm: false, sendingFormId: null });
        toast.success('Form sent');
        // Refresh booking detail to get updated linkedForms
        this.fetchDetail();
      })
      .catch(() => {
        this.setState({ sendingFormId: null });
        toast.error('Could not send form');
      });
  }

  loadClientData = () => {
    const { bookingDetail } = this.state;
    const clientId = bookingDetail?.user?._id || bookingDetail?.userId;
    if (!clientId || clientId === WALKIN_CLIENT_ID) return;

    this.setState({ clientLoading: true, clientError: false });

    const hairdresserId = localStorage.getItem('userId');
    hairdresserService.getClientDetail({ hairdresserId, userId: clientId })
      .then(response => {
        const data = response?.data?.data || response?.data?.responseData || response?.data;
        this.setState({ clientData: data, clientLoading: false });
      })
      .catch(() => {
        this.setState({ clientLoading: false, clientError: true });
      });

    this.loadWallet(clientId);
  }

  // Wallet section of the Client tab: the salon's scheme (hydrated fresh so
  // a cold cache never misreports "no scheme") plus this client's real
  // progress card. Independent of loadClientData's own request/error state
  // so a wallet hiccup never blocks the rest of the tab from rendering.
  loadWallet = (clientId) => {
    hydrateLoyalty(loyaltyOwnerId())
      .then(({ scheme }) => { if (!this._unmounted) this.setState({ loyaltyScheme: scheme || null, loyaltySchemeLoaded: true }); })
      .catch(() => { if (!this._unmounted) this.setState({ loyaltyScheme: loadLoyaltyScheme(loyaltyOwnerId()), loyaltySchemeLoaded: true }); });

    marketingBackend.loyaltyClientCards(clientId)
      .then((res) => { if (!this._unmounted) this.setState({ loyaltyClientCard: (res && res.cards && res.cards[0]) || null, loyaltyCardLoaded: true }); })
      .catch(() => { if (!this._unmounted) this.setState({ loyaltyClientCard: null, loyaltyCardLoaded: true }); });
  }

  renderClientTab(detail, currency, isWalkin) {
    const { clientData, clientLoading, clientError, loyaltyScheme, loyaltySchemeLoaded, loyaltyClientCard, loyaltyCardLoaded } = this.state;
    const user = detail.user || {};

    // Reception mode: a locked front desk whose role has no client notes and
    // history sees the name only (no spend, visits, notes, photos, wallet).
    if (isReceptionHidden('clientDetails')) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || 'Client';
      return (
        <div className="bdp-client-tab">
          <p className="bdp-recent-name">{name}</p>
          <p className="bdp-tab-loading">Client details are hidden while this device is in reception mode.</p>
        </div>
      );
    }

    if (clientLoading && !clientData) {
      return (
        <div className="bdp-tab-loading">
          <div className="bdp-loading-spinner" />
          <span>Loading client info...</span>
        </div>
      );
    }

    if (clientError) {
      return (
        <div className="bdp-tab-loading">
          <span>Could not load client info</span>
          <button type="button" className="bdp-note-edit-btn" onClick={() => this.loadClientData()} style={{ marginTop: 8 }}>Retry</button>
        </div>
      );
    }

    // Extract stats from clientData
    const counts = Array.isArray(clientData?.count) ? clientData.count : [];
    const totalVisits = counts.reduce((sum, c) => sum + (c.count || 0), 0);
    const cancellations = counts.find(c => c._id === 3)?.count || 0;
    const noShows = counts.find(c => c._id === 6)?.count || 0;
    const totalSpend = parseFloat(clientData?.totalServiceSales) || 0;
    const vouchers = clientData?.clientDetail?.voucherPurchases || [];
    const voucherTotal = vouchers.reduce((s, v) => s + (v.amount || 0), 0);

    const upcomingBookings = clientData?.upcomingBookings || [];
    const pastBookings = clientData?.pastBooking || [];

    const renderBookingList = (bookings, limit = 3) => (
      <ul className="bdp-recent-bookings">
        {bookings.slice(0, limit).map((b, i) => {
          const bServices = b.servicesBooked || [];
          const bName = bServices[0]?.service?.serviceName || bServices[0]?.serviceName || 'Booking';
          const bTotal = parseFloat(b.totalAmount) || bServices.reduce((s, sv) => s + (parseFloat(sv.servicePrice) || 0), 0);
          const bDate = b.bookingDate ? moment(b.bookingDate).format('D MMM') : '';
          const bId = b._id;
          const bStatus = getStatus(b.bookingStatus ?? b.bookingStatusId);
          // Online booking the client never secured (deposit unpaid / card not
          // captured). Mirror the /clients panel: show "Not booked yet", or
          // "Abandoned" once it's older than the grace window — not "Booked".
          const bUnsecured = b.abandonedCheckout === true || (
            b.isOnlineBooking === 1 &&
            b.bookingStatus !== 3 && b.bookingStatus !== 6 &&
            (
              (b.isDeposit === 1 && b.isDepositPay !== 1) ||
              (b.isCardCapture === 1 && !(b.stripePaymentMethodId != null && b.stripePaymentMethodId !== ''))
            )
          );
          const bCreatedAt = b.createdAt || b.createdTime;
          const bAgeHrs = bCreatedAt ? (Date.now() - new Date(bCreatedAt).getTime()) / 3600000 : 0;
          const bUnsecuredLabel = (bUnsecured && bAgeHrs >= 24) ? 'Abandoned' : 'Not booked yet';
          return (
            <li key={i} className={'bdp-recent-booking' + (bUnsecured ? ' bdp-recent-booking--static' : '')} onClick={bUnsecured ? undefined : () => {
              if (bId) this.handleViewBookingInPanel(bId, b);
            }}>
              <span className="bdp-recent-date">{bDate}</span>
              <span className="bdp-recent-name">{bName}{bServices.length > 1 ? ` + ${bServices.length - 1}` : ''}</span>
              <span className={`bdp-recent-status ${bUnsecured ? 'bdp-status--abandoned' : bStatus.css}`}>{bUnsecured ? bUnsecuredLabel : bStatus.label}</span>
              {!bUnsecured && <RefundBadge sale={b} small />}
              <span className="bdp-recent-price">{currency}{bTotal.toFixed(2)}</span>
            </li>
          );
        })}
      </ul>
    );

    return (
      <div className="bdp-client-tab">
        {/* View Profile link (hidden for walk-ins) */}
        {user._id && !isWalkin && (
          <button
            type="button"
            className="bdp-view-profile-btn"
            onClick={() => this.setState({ clientPanelOpen: true })}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M2 12.5C2 10.29 4.24 8.5 7 8.5C9.76 8.5 12 10.29 12 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            View full client profile
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 'auto' }}>
              <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Contact info */}
        <div className="bdp-section">
          <h4 className="bdp-section-title">Contact</h4>
          <div className="bdp-client-contact">
            {user.email && !user.email.includes('@beuapp.com') && (
              <div className="bdp-contact-row">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.1"/><path d="M1 4.5L7 8L13 4.5" stroke="currentColor" strokeWidth="1.1"/></svg>
                <span>{user.email}</span>
              </div>
            )}
            {user.phoneNumber && (
              <div className="bdp-contact-row">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.5 1.5H3.5C2.67 1.5 2 2.17 2 3V11C2 11.83 2.67 12.5 3.5 12.5H10.5C11.33 12.5 12 11.83 12 11V3C12 2.17 11.33 1.5 10.5 1.5H8.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/><path d="M5.5 10.5H8.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>
                <span>{user.phoneNumber}</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        {clientData && (
          <div className="bdp-section">
            <h4 className="bdp-section-title">Stats</h4>
            <div className="bdp-stats-grid">
              <div className="bdp-stat">
                <span className="bdp-stat-value">{totalVisits}</span>
                <span className="bdp-stat-label">Visits</span>
              </div>
              <div className="bdp-stat">
                <span className="bdp-stat-value">{currency}{totalSpend.toFixed(2)}</span>
                <span className="bdp-stat-label">Total Spend</span>
              </div>
              <div className="bdp-stat">
                <span className="bdp-stat-value">{cancellations}</span>
                <span className="bdp-stat-label">Cancellations</span>
              </div>
              <div className="bdp-stat">
                <span className="bdp-stat-value">{noShows}</span>
                <span className="bdp-stat-label">No Shows</span>
              </div>
            </div>
          </div>
        )}

        {/* Photos — inline strip so stylists can scan history + add new
            shots without leaving the slide-out. Hidden for walk-ins since
            they have no persistent client record to attach to.
            Currently gated behind CLIENT_PHOTOS_ENABLED until the
            client-flow migration populates SalonClient. */}
        {CLIENT_PHOTOS_ENABLED && user._id && !isWalkin && (
          <BookingPhotosInline
            clientId={user._id}
            clientName={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.name}
            bookingId={detail._id}
            onSeeAll={() => this.setState({ clientPanelOpen: true, clientPanelInitialTab: 'photos' })}
          />
        )}

        {/* Wallet: loyalty progress + gift cards, so front-of-house can see
            what this client holds without leaving the booking panel.
            Hidden for walk-ins (no persistent client record). */}
        {user._id && !isWalkin && (
          <div className="bdp-section">
            <div className="bdp-section-header-row">
              <h4 className="bdp-section-title">Wallet</h4>
              {(loyaltyClientCard || vouchers.length > 0) && (
                <button type="button" className="bdp-note-edit-btn" onClick={() => this.setState({ clientPanelOpen: true, clientPanelInitialTab: 'wallet' })}>
                  View all
                </button>
              )}
            </div>

            {/* Loyalty */}
            {!loyaltySchemeLoaded || !loyaltyCardLoaded ? (
              <p className="bdp-note bdp-note--empty">Loading…</p>
            ) : !loyaltyScheme ? (
              <p className="bdp-note bdp-note--empty">No loyalty scheme yet</p>
            ) : (() => {
              const isPoints = loyaltyScheme.mode === 'points';
              const stampsReq = loyaltyScheme.stampsRequired || 6;
              const rewardsAvailable = (loyaltyClientCard && loyaltyClientCard.rewardsAvailable) || 0;
              const filled = loyaltyClientCard ? (rewardsAvailable > 0 ? stampsReq : ((loyaltyClientCard.filled || 0) % stampsReq)) : 0;
              const balance = (loyaltyClientCard && loyaltyClientCard.balance) || 0;
              const progressLabel = isPoints
                ? `${balance.toLocaleString()} of ${(loyaltyScheme.pointsTarget || 500).toLocaleString()} points`
                : `${filled} of ${stampsReq} stamps`;
              return (
                <div className="bdp-wallet-loyalty-row" onClick={() => this.setState({ clientPanelOpen: true, clientPanelInitialTab: 'wallet' })}>
                  <span className={`bdp-wallet-loyalty-dot${rewardsAvailable > 0 ? ' bdp-wallet-loyalty-dot--ready' : ''}`} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="bdp-wallet-loyalty-progress">{progressLabel}</p>
                    <p className={`bdp-wallet-loyalty-reward${rewardsAvailable > 0 ? ' bdp-wallet-loyalty-reward--ready' : ''}`}>
                      {rewardsAvailable > 0 ? 'Reward ready to redeem' : (isPoints ? loyaltyScheme.pointsLabel : loyaltyScheme.rewardLabel)}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Gift cards */}
            {vouchers.length === 0 ? (
              <p className="bdp-note bdp-note--empty" style={{ marginTop: 8 }}>No gift cards yet</p>
            ) : (
              <>
                <ul className="bdp-recent-bookings" style={{ marginTop: 4 }}>
                  {vouchers.slice(0, 3).map((v, i) => (
                    <li key={v._id || i} className="bdp-recent-booking bdp-recent-booking--static">
                      <span className="bdp-recent-name">{v.voucherName || 'Gift card'}</span>
                      <span className="bdp-recent-date">{v.purchaseDate ? moment(v.purchaseDate).format('D MMM') : ''}</span>
                      <span className="bdp-recent-price">{currency}{(v.amount || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: 12, color: '#8E8E93', margin: '4px 8px 0' }}>
                  {vouchers.length} card{vouchers.length === 1 ? '' : 's'} · {currency}{voucherTotal.toFixed(2)} total
                </p>
              </>
            )}
          </div>
        )}

        {/* Client Note */}
        <div className="bdp-section">
          <div className="bdp-section-header-row">
            <h4 className="bdp-section-title">Client Note</h4>
            {!this.state.editingClientNote && (
              <button type="button" className="bdp-note-edit-btn" onClick={() => {
                const note = user.note || clientData?.clientDetail?.note || clientData?.note || '';
                this.setState({ editingClientNote: true, clientNoteValue: note });
              }}>
                {(user.note || clientData?.clientDetail?.note || clientData?.note) ? 'Edit' : 'Add'}
              </button>
            )}
          </div>
          {this.state.editingClientNote ? (
            <div className="bdp-note-editor">
              <textarea
                className="bdp-note-textarea"
                rows={3}
                value={this.state.clientNoteValue}
                onChange={(e) => this.setState({ clientNoteValue: e.target.value })}
                placeholder="Add notes about this client..."
                autoFocus
              />
              <div className="bdp-note-editor-actions">
                <button type="button" className="bdp-note-cancel" onClick={() => this.setState({ editingClientNote: false })}>Cancel</button>
                <button type="button" className="bdp-note-save" onClick={this.handleSaveClientNote} disabled={this.state.noteSaving}>
                  {this.state.noteSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <p className={`bdp-note ${!(user.note || clientData?.clientDetail?.note || clientData?.note) ? 'bdp-note--empty' : ''}`}>
              {user.note || clientData?.clientDetail?.note || clientData?.note || 'No client notes yet'}
            </p>
          )}
        </div>

        {/* Booking Note */}
        <div className="bdp-section">
          <div className="bdp-section-header-row">
            <h4 className="bdp-section-title">Booking Note</h4>
            {!this.state.editingBookingNote && (
              <button type="button" className="bdp-note-edit-btn" onClick={() => {
                this.setState({ editingBookingNote: true, bookingNoteValue: detail.bookingNote || '' });
              }}>
                {detail.bookingNote ? 'Edit' : 'Add'}
              </button>
            )}
          </div>
          {this.state.editingBookingNote ? (
            <div className="bdp-note-editor">
              <textarea
                className="bdp-note-textarea"
                rows={3}
                value={this.state.bookingNoteValue}
                onChange={(e) => this.setState({ bookingNoteValue: e.target.value })}
                placeholder="Add notes about this booking..."
                autoFocus
              />
              <div className="bdp-note-editor-actions">
                <button type="button" className="bdp-note-cancel" onClick={() => this.setState({ editingBookingNote: false })}>Cancel</button>
                <button type="button" className="bdp-note-save" onClick={this.handleSaveBookingNote} disabled={this.state.noteSaving}>
                  {this.state.noteSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <p className={`bdp-note ${!detail.bookingNote ? 'bdp-note--empty' : ''}`}>
              {detail.bookingNote || 'No booking notes yet'}
            </p>
          )}
        </div>

        {/* Forms linked to this booking */}
        {(() => {
          const allForms = [];
          (detail.servicesBooked || []).forEach(s => {
            (s.linkedForms || []).forEach(f => {
              if (!allForms.find(af => af.formId === f.formId)) allForms.push(f);
            });
          });
          const pendingCount = allForms.filter(f => !f.isCompleted).length;
          return (
            <div className="bdp-section">
              <div className="bdp-section-header-row">
                <h4 className="bdp-section-title">Forms</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {pendingCount > 0 && <span className="bdp-form-badge">{pendingCount} pending</span>}
                  {!isWalkin && <button type="button" className="bdp-note-edit-btn" onClick={this.handleOpenSendForm}>Add</button>}
                </div>
              </div>
              {allForms.length === 0 ? (
                <p className="bdp-note bdp-note--empty">No forms linked to this booking</p>
              ) : (
              <ul className="bdp-form-list">
                {allForms.map((f, i) => (
                  <li key={i} className="bdp-form-item">
                    {f.isCompleted ? (
                      <svg className="bdp-form-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6" stroke="#007EA0" strokeWidth="1.2"/>
                        <path d="M4.5 7L6.5 9L9.5 5" stroke="#007EA0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg className="bdp-form-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6" stroke="#ccc" strokeWidth="1.2"/>
                      </svg>
                    )}
                    <span className="bdp-form-name">{f.formName || 'Form'}</span>
                    <span className={`bdp-form-status ${f.isCompleted ? 'bdp-form-status--done' : ''}`}>
                      {f.isCompleted ? 'Completed' : 'Pending'}
                    </span>
                  </li>
                ))}
              </ul>
              )}
            </div>
          );
        })()}

        {/* Upcoming bookings */}
        {upcomingBookings.length > 0 && (
          <div className="bdp-section">
            <div className="bdp-section-header-row">
              <h4 className="bdp-section-title">Upcoming</h4>
              <span className="bdp-booking-count">{upcomingBookings.length}</span>
            </div>
            {renderBookingList(upcomingBookings)}
          </div>
        )}

        {/* Past bookings */}
        {pastBookings.length > 0 && (
          <div className="bdp-section">
            <div className="bdp-section-header-row">
              <h4 className="bdp-section-title">Past Bookings</h4>
              <span className="bdp-booking-count">{pastBookings.length}</span>
            </div>
            {renderBookingList(pastBookings, this.state.pastBookingsVisible)}
            {pastBookings.length > this.state.pastBookingsVisible && (
              <button
                type="button"
                className="bdp-show-more-btn"
                onClick={() => this.setState(prev => ({ pastBookingsVisible: prev.pastBookingsVisible + 10 }))}
              >
                Show more
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  renderActivityTab(detail, currency, event) {
    // Build timeline from available timestamps
    const events = [];

    if (detail.createdTime) {
      events.push({
        time: detail.createdTime,
        icon: 'create',
        label: isOnlineBookingDetail(detail, event) ? 'Booking created (online)' : 'Booking created (in-salon)',
      });
    }

    // Booking-level deposit-paid event — fallback only. Skip when the
    // deposit is already represented by a row in transaction[] below
    // (modern bookings with per-row tagging). Otherwise this duplicates
    // the deposit on the Activity timeline. Legacy bookings without a
    // per-row deposit still get this entry so the deposit isn't missing.
    const hasDepositRow = (detail.transaction || []).some(t => t.isDeposit || t.pgObjectType === 'deposit');
    if (detail.isDeposit && detail.isDepositPay && detail.depositAmount > 0 && !hasDepositRow) {
      events.push({
        time: detail.createdTime, // deposit paid at creation typically
        icon: 'payment',
        label: `Deposit ${currency}${parseFloat(detail.depositAmount).toFixed(2)} paid`,
      });
    }

    if (detail.updatedTime && detail.createdTime && detail.updatedTime !== detail.createdTime) {
      events.push({
        time: detail.updatedTime,
        icon: 'edit',
        label: 'Booking updated',
      });
    }

    const status = detail.bookingStatus;
    if (status === 2 && detail.updatedTime) {
      events.push({
        time: detail.updatedTime,
        icon: 'confirm',
        label: 'Booking confirmed',
      });
    }

    if (status === 4 && detail.bookingCompleteDate) {
      events.push({
        time: detail.bookingCompleteDate,
        icon: 'complete',
        label: 'Booking completed',
      });
    }

    if (status === 3 && (detail.cancelledDate || detail.updatedTime)) {
      // cancelledByType/cancelledByName come from the BE: 'client' (client
      // cancelled via the app) or 'salon' (+ staff name when the v2
      // salon-cancel recorded who). Legacy cancels fall back to the plain label.
      const cancelLabel = detail.cancelledByType === 'client'
        ? 'Cancelled by client'
        : detail.cancelledByType === 'salon'
          ? (detail.cancelledByName ? `Cancelled by ${detail.cancelledByName}` : 'Cancelled by salon')
          : 'Booking cancelled';
      events.push({
        time: detail.cancelledDate || detail.updatedTime,
        icon: 'cancel',
        label: cancelLabel,
      });
    }

    if (detail.depositRefunded && detail.depositRefundedAmount > 0) {
      events.push({
        time: detail.depositRefundDate || detail.cancelledDate || detail.updatedTime,
        icon: 'payment',
        label: `Deposit ${currency}${parseFloat(detail.depositRefundedAmount).toFixed(2)} refunded to client`,
      });
    }

    // Refunds made on a completed sale (docs/refunds.md refunds[]), dated when made.
    // Deposit refunds on cancellation already show above.
    (Array.isArray(detail.refunds) ? detail.refunds : []).forEach(r => {
      const st = String(r.status || 'succeeded').toLowerCase();
      if (st === 'failed' || !(Math.abs(parseFloat(r.amount) || 0) > 0)) return;
      if (status !== 4 && detail.depositRefunded) return;
      const how = { card: 'card', cash: 'cash', voucher: 'gift card' }[r.method] || 'other';
      events.push({
        time: r.at || r.recordedAt || detail.updatedTime,
        icon: 'refund',
        label: `Refund ${currency}${Math.abs(parseFloat(r.amount)).toFixed(2)} (${how})${st === 'pending' ? ', pending' : ''}${r.byName ? ` by ${r.byName}` : ''}`,
      });
    });

    if (status === 6 && detail.noShowDate) {
      events.push({
        time: detail.noShowDate,
        icon: 'noshow',
        label: 'Marked as no show',
      });
    }

    // Payment transactions — use the per-row createdAt where available so
    // deposits and final captures show on their actual timeline (vs all
    // collapsed to the booking-complete date). Falls back to booking-level
    // dates for legacy rows that pre-date per-row createdAt.
    const paymentLabels = { 1: 'Online', 2: 'Card', 3: 'Cash', 4: 'Other', 5: 'Card Reader', 10: 'Card on File', 11: 'Voucher', 12: 'Payment Link', 13: 'Klarna', 14: 'Clearpay', 15: 'Tap to Pay' };
    (detail.transaction || []).forEach(t => {
      const label = paymentLabels[t.paymentType] || 'Payment';
      const isDeposit = !!t.isDeposit || t.pgObjectType === 'deposit';
      events.push({
        time: t.createdAt || detail.bookingCompleteDate || detail.updatedTime || detail.createdTime,
        icon: 'payment',
        label: `${isDeposit ? 'Deposit' : 'Payment'} ${currency}${parseFloat(t.amount || 0).toFixed(2)} (${label})`,
      });
    });

    // Sort chronologically (newest first)
    events.sort((a, b) => new Date(b.time) - new Date(a.time));

    const ICON_MAP = {
      create: { color: '#007EA0', symbol: '+' },
      payment: { color: '#16a34a', symbol: currency[0] || '£' },
      edit: { color: '#e67e22', symbol: '✎' },
      confirm: { color: '#007EA0', symbol: '✓' },
      complete: { color: '#16a34a', symbol: '✓' },
      cancel: { color: '#dc2626', symbol: '✕' },
      noshow: { color: '#dc2626', symbol: '!' },
      refund: { color: '#b45309', symbol: '↩' },
    };

    return (
      <div className="bdp-activity-tab">
        {events.length === 0 ? (
          <div className="bdp-tab-empty">No activity recorded yet</div>
        ) : (
          <div className="bdp-timeline">
            {events.map((e, i) => {
              const icon = ICON_MAP[e.icon] || ICON_MAP.create;
              return (
                <div key={i} className="bdp-timeline-item">
                  <div className="bdp-timeline-node" style={{ background: icon.color }}>
                    <span>{icon.symbol}</span>
                  </div>
                  {i < events.length - 1 && <div className="bdp-timeline-line" />}
                  <div className="bdp-timeline-content">
                    <span className="bdp-timeline-time">{e.time && moment(e.time).isValid() ? moment(e.time).format('D MMM YYYY, HH:mm') : 'Time unknown'}</span>
                    <span className="bdp-timeline-label">{e.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="bdp-activity-note">
          Communication logs (SMS reminders, emails) will be available in a future update.
        </div>
      </div>
    );
  }

  renderBooking() {
    const { event: propsEvent, currencySymbol: propCurrency } = this.props;
    const { bookingDetail, menuOpen } = this.state;
    const detail = bookingDetail || {};
    // When viewing a stacked booking (from client history), use bookingDetail for event data
    const isStacked = this.state.panelHistory.length > 0;
    const event = isStacked ? {
      ...propsEvent,
      startMinutes: detail.bookingSlot?.[0] ?? propsEvent.startMinutes,
      endMinutes: detail.bookingSlot?.[1] ?? propsEvent.endMinutes,
      bookingId: detail._id || propsEvent.bookingId,
      clientName: detail.user?.name || propsEvent.clientName,
      clientAvatar: detail.user?.avatar || propsEvent.clientAvatar,
      clientId: detail.user?._id || propsEvent.clientId,
      status: detail.bookingStatus ?? propsEvent.status,
      date: detail.bookingDate || propsEvent.date,
      // Must NOT inherit the root event's flag: that belongs to a different
      // booking. Use the flag captured from the list row that opened this view.
      isOnlineBooking: this.state.stackedIsOnlineBooking,
    } : {
      ...propsEvent,
      // The detail call is the source of truth for the slot. The event the
      // panel was opened from may not carry real minutes (e.g. opened from a
      // client's bookings list rather than the diary grid), so prefer the
      // fetched bookingSlot and only fall back to the event. Same rule the
      // stacked branch above and the edit-mode seeding already follow.
      startMinutes: detail.bookingSlot?.[0] ?? propsEvent?.startMinutes,
      endMinutes: detail.bookingSlot?.[1] ?? propsEvent?.endMinutes,
    };

    // Walk-in detection
    const isWalkin = (detail.user?._id === WALKIN_CLIENT_ID) ||
                     (event.clientId === WALKIN_CLIENT_ID) ||
                     !detail.user;

    const clientName = isWalkin ? 'Walk-in' : (detail.user?.name || event.clientName || 'Client');
    const clientAvatar = isWalkin ? null : (detail.user?.localAvatar || detail.user?.avatar || event.clientAvatar);
    const clientEmail = detail.user?.email;
    const clientPhone = detail.user?.phoneNumber;
    // Walk-in emails are synthetic (e.g. xxx@beuapp.com), hide those
    const hasValidEmail = !isWalkin && clientEmail && !clientEmail.includes('@beuapp.com');
    const hasValidPhone = !isWalkin && clientPhone;

    const status = detail.bookingStatus ?? event.status;
    const { label: statusLabel, css: statusClass } = getStatus(status);

    const services = (detail.servicesBooked != null ? detail.servicesBooked : event.services) || [];

    // Currency: prefer API response, then prop from Redux, then fallback
    const currency = detail.currencySymbol || detail.hairdresser?.hairdresser?.currencySymbol || propCurrency || '£';

    const bookingDate = detail.bookingDate || event.date;
    const formattedDate = moment(bookingDate).format('ddd D MMM YYYY');

    // Multi-service: gather unique staff names across services
    const staffNames = [];
    if (detail.hairdresser?.name) staffNames.push(detail.hairdresser.name);
    services.forEach(s => {
      const name = s.hairdresserName || s.hairdresser?.name || s.staff?.name;
      if (name && !staffNames.includes(name)) staffNames.push(name);
    });

    const isActionable = status !== 3 && status !== 4 && status !== 6;
    // Refund a completed sale. Whether this person may (seller rule,
    // `refunds` permission) is the server's call: the panel explains a refusal.
    // Permission (seller rule / `refunds`) AND, on a locked front desk, its role.
    const showRefund = REFUNDS_VISIBLE && status === 4 && !!detail._id && canOfferRefund(detail, { completed: true }) && canSeeSaleRefunds(this._moneyActor()) && !isReceptionHidden('refunds');

    return (
      <>
        {/* Header */}
        <div className="bdp-header">
          <div className="bdp-header-left">
            {this.state.panelHistory.length > 0 && (
              <button className="bdp-back-btn" onClick={this.handlePanelBack}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {clientAvatar ? (
              <img className="bdp-avatar" src={clientAvatar} alt="" onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <div className={`bdp-avatar bdp-avatar--initials ${isWalkin ? 'bdp-avatar--walkin' : ''}`}>
                {isWalkin ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 3C8.34 3 7 4.34 7 6C7 7.66 8.34 9 10 9C11.66 9 13 7.66 13 6C13 4.34 11.66 3 10 3Z" fill="currentColor"/>
                    <path d="M10 11C6.69 11 4 12.79 4 15V17H16V15C16 12.79 13.31 11 10 11Z" fill="currentColor"/>
                  </svg>
                ) : (clientName || '?')[0]}
              </div>
            )}
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">{clientName}</h3>
              {hasValidEmail && <span className="bdp-client-sub">{clientEmail}</span>}
              {hasValidPhone && <span className="bdp-client-sub">{clientPhone}</span>}
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="bdp-tab-bar">
          {['details', 'client', 'activity'].map(tab => (
            <button
              key={tab}
              type="button"
              className={`bdp-tab ${this.state.activeTab === tab ? 'bdp-tab--active' : ''}`}
              onClick={() => {
                this.setState({ activeTab: tab });
                if (tab === 'client' && !this.state.clientData && !this.state.clientLoading) {
                  this.loadClientData();
                }
              }}
            >
              {tab === 'details' ? 'Details' : tab === 'client' ? 'Client' : 'Activity'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bdp-body">
          {this.state.activeTab === 'client' && this.renderClientTab(detail, currency, isWalkin)}
          {this.state.activeTab === 'activity' && this.renderActivityTab(detail, currency, event)}
          {this.state.activeTab === 'details' && detail.seriesId && this.state.series && (
            <div className="bdp-section rpt-series" role="note">
              <svg className="rpt-series-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 3v3h-3M13 8a5 5 0 0 1-8.5 3.5L3 10M3 13v-3h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>
                {this.state.series.status === 'stopped' ? 'Stopped repeating' : `Repeats ${this.state.series.said}`}
                {this.state.series.upcoming > 0 ? ` · ${this.state.series.upcoming} ${this.state.series.status === 'stopped' ? 'still booked' : 'upcoming'}` : ''}
                {this.state.series.alerts && this.state.series.alerts.length > 0 ? ` · ${this.state.series.alerts.length} date${this.state.series.alerts.length === 1 ? '' : 's'} couldn’t be booked` : ''}
                {/* Just booked, by this person: Undo, for the 10 minutes it's allowed */}
                {this.state.series.status === 'active' && canUndo(detail.seriesId) && (
                  <span className="rpt-undo">
                    Booked by mistake? <button type="button" className="rpt-link" onClick={this.handleUndoRepeat} disabled={this.state.undoing}>{this.state.undoing ? 'Undoing...' : 'Undo this repeat'}</button>. Every visit goes and nothing is sent to the client. {undoLeftSaid(undoLeft(detail.seriesId))}.
                  </span>
                )}
              </span>
            </div>
          )}
          {this.state.activeTab === 'details' && <div className="bdp-details-tab">
          <div className="bdp-meta-row">
            <span className={`bdp-status ${statusClass}`}>{statusLabel}</span>
            {status === 4 && <RefundBadge sale={detail} />}
            <span className="bdp-meta-date">{formattedDate}</span>
          </div>
          {/* The salon asked the self-employed seller to refund this sale
              (docs/refunds.md §4, booking.refundRequests). */}
          {showRefund && (() => {
            const open = (Array.isArray(detail.refundRequests) ? detail.refundRequests : []).filter(r => (r.status || 'open') === 'open');
            if (!open.length) return null;
            const r = open[open.length - 1];
            return (
              <div style={{ margin: '12px 0 4px', padding: '12px 14px', borderRadius: '10px', background: '#fef7e6', color: '#7a4b00', fontSize: '13px', lineHeight: 1.5 }}>
                {r.byName || 'The salon'} asked you to refund this sale{r.amount > 0 ? ` (${currency}${Number(r.amount).toFixed(2)})` : ''}.
                {r.note ? <span style={{ display: 'block', marginTop: '4px', color: '#8a6a2a' }}>{r.note}</span> : null}
              </div>
            );
          })()}
          <div className="bdp-time-row">
            <svg className="bdp-time-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M7 3.5V7L9.5 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span>{formatTime(event.startMinutes)} - {formatTime(event.endMinutes)}</span>
            <span className="bdp-duration">{(() => { const d = event.endMinutes - event.startMinutes; const h = Math.floor(d / 60); const m = d % 60; if (h === 0) return `${m} min`; if (m === 0) return `${h}h`; return `${h}h ${m}m`; })()}</span>
          </div>
          {staffNames.length > 0 && (
            <div className="bdp-staff-row">
              <svg className="bdp-staff-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M2 12.5C2 10.29 4.24 8.5 7 8.5C9.76 8.5 12 10.29 12 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span>{staffNames.join(', ')}</span>
            </div>
          )}

          {/* Services */}
          {services.length > 0 && (
            <div className="bdp-section">
              <h4 className="bdp-section-title">Services</h4>
              <ul className="bdp-services">
                {services.map((s, i) => {
                  const svcStaff = s.hairdresserName || s.hairdresser?.name || s.staff?.name;
                  return (
                    <li key={i} className="bdp-service-item">
                      <span className="bdp-service-color" style={{ background: s.service?.diaryColour || s.diaryColour || event.diaryColour }} />
                      <div className="bdp-service-info">
                        <span className="bdp-service-name">{s.service?.serviceName || s.serviceName || s.service?.name}</span>
                        {svcStaff && (
                          <span className="bdp-service-staff">{svcStaff}</span>
                        )}
                      </div>
                      <span className="bdp-service-price">{currency}{parseFloat(s.servicePrice || 0).toFixed(2)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Room / equipment held (docs/resources.md §5a). Rendered from the
              diary event's snapshots; options load lazily on expand. */}
          {Array.isArray(event.resources) && event.resources.length > 0 && (
            <div className="bdp-section">
              <h4 className="bdp-section-title">Room</h4>
              {event.resources.map((r, i) => {
                const opts = (this.state.roomOptions || []).find(g => String(g.groupId) === String(r.groupId));
                const expanded = this.state.roomExpanded === String(r.groupId);
                return (
                  <div key={i} className="bdp-room-row">
                    <div className="bdp-room-line">
                      <span className="bdp-room-dot" style={{ background: r.diaryColour || '#8B8B93' }} />
                      <span className="bdp-room-name">{r.resourceName}</span>
                      {r.pinned && <span className="bdp-room-pin" title="Pinned — Beu won't move this booking to another room">📌</span>}
                      <button className="bdp-room-change" onClick={() => {
                        const g = String(r.groupId);
                        this.setState({ roomExpanded: expanded ? null : g });
                        if (!expanded && !this.state.roomOptions) this.loadRoomOptions();
                      }}>{expanded ? 'Close' : 'Change'}</button>
                    </div>
                    {expanded && (
                      !opts ? <div className="bdp-room-loading">Checking what's free…</div> : (
                        <div className="bdp-room-opts">
                          {opts.requiredResourceId && (
                            <div className="bdp-room-loading">This service must use {opts.current.resourceName} — set on the service, not the booking.</div>
                          )}
                          {opts.options.map(o => (
                            <button key={String(o.resourceId)} disabled={!o.free || this.state.roomSaving}
                              className={`bdp-room-opt${String(o.resourceId) === String(opts.current.resourceId) ? ' bdp-room-opt--current' : ''}`}
                              onClick={() => this.saveRoomChange(String(r.groupId), String(o.resourceId), opts.current.pinned)}>
                              {o.name}{String(o.resourceId) === String(opts.current.resourceId) ? ' · current' : (o.free ? '' : ' · busy')}
                            </button>
                          ))}
                          {!opts.requiredResourceId && <label className="bdp-room-pinrow">
                            <input type="checkbox" defaultChecked={opts.current.pinned} disabled={this.state.roomSaving}
                              onChange={(e) => this.saveRoomChange(String(r.groupId), String(opts.current.resourceId), e.target.checked)} />
                            <span>Keep this exact room — Beu won't move it when juggling the day</span>
                          </label>}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Products (if any) */}
          {(() => {
            const products = detail.productIds || detail.products || [];
            if (products.length === 0) return null;
            return (
              <div className="bdp-section">
                <h4 className="bdp-section-title">Products</h4>
                <ul className="bdp-services">
                  {products.map((p, i) => (
                    <li key={i} className="bdp-service-item">
                      <span className="bdp-service-color" style={{ background: '#e8dff0' }} />
                      <div className="bdp-service-info">
                        <span className="bdp-service-name">{p.productName || p.name || 'Product'}</span>
                      </div>
                      <span className="bdp-service-price">{currency}{parseFloat(p.retailPrice || p.price || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Payment summary */}
          {(() => {
            const products = detail.productIds || detail.products || [];
            const vouchers = detail.voucherIds || detail.vouchers || [];
            const transactions = detail.transaction || [];
            const serviceTotal = services.reduce((sum, s) => sum + (parseFloat(s.servicePrice) || 0), 0);
            const productTotal = products.reduce((sum, p) => sum + (parseFloat(p.retailPrice || p.price) || 0), 0);
            const voucherTotal = vouchers.reduce((sum, v) => sum + (parseFloat(v.value || v.amount) || 0), 0);
            // Always compute from live service/product/voucher totals. detail.totalAmount
            // is a cached services-only sum that goes stale whenever services are edited,
            // and for product-only POS bookings it is 0 by design.
            const grandTotal = serviceTotal + productTotal - voucherTotal;
            let totalPaid = 0;
            // Calculate total paid from the same transaction source used for display.
            // Card-reader legs count too — a part-paid reader sale left the whole
            // total showing as outstanding when they were skipped here.
            transactions.forEach(t => { totalPaid += parseFloat(t.amount) || 0; });
            const balance = grandTotal - totalPaid;
            const PAYMENT_LABELS = { 1: 'Online', 2: 'Card', 3: 'Cash', 4: 'Other', 5: 'Card Reader', 10: 'Card on File', 11: 'Voucher', 12: 'Payment Link', 13: 'Klarna', 14: 'Clearpay', 15: 'Tap to Pay' };
            const REFUND_METHODS = { card: 'card', cash: 'cash', voucher: 'gift card' };
            // Taken at the desk, so never a deposit even on an open booking.
            const IN_SALON_METHODS = [5, 15];
            const tip = parseFloat(detail.tipAmount) || 0;
            const saleRefunds = (Array.isArray(detail.refunds) ? detail.refunds : [])
              .filter(r => String(r.status || 'succeeded').toLowerCase() !== 'failed' && Math.abs(parseFloat(r.amount) || 0) > 0);
            const refundState = refundStatusOf(detail);

            // Only show if there's meaningful payment data
            if (transactions.length === 0 && totalPaid === 0 && grandTotal === 0) return null;

            return (
              <div className="bdp-section">
                <h4 className="bdp-section-title">Payment</h4>
                <div className="bdp-payment">
                  {/* Subtotals (only if products or vouchers exist) */}
                  {(productTotal > 0 || voucherTotal > 0) && (
                    <>
                      <div className="bdp-payment-row">
                        <span>Services</span>
                        <span>{currency}{serviceTotal.toFixed(2)}</span>
                      </div>
                      {productTotal > 0 && (
                        <div className="bdp-payment-row">
                          <span>Products</span>
                          <span>{currency}{productTotal.toFixed(2)}</span>
                        </div>
                      )}
                      {voucherTotal > 0 && (
                        <div className="bdp-payment-row bdp-payment-row--discount">
                          <span>Voucher</span>
                          <span>-{currency}{voucherTotal.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="bdp-payment-row bdp-payment-row--total">
                    <span>Total</span>
                    <span>{currency}{grandTotal.toFixed(2)}</span>
                  </div>
                  {/* Payment methods. Gift-card redemptions (type 11) are a
                      payment in their own right — never label them as the
                      deposit method even on upcoming bookings. */}
                  {/* Every payment leg, card reader included (a reader sale
                      used to list nothing). The tip rides on the card leg. */}
                  {transactions.map((t, i) => (
                    <div key={i} className="bdp-payment-row bdp-payment-row--method">
                      <span>
                        {t.paymentType === 11 ? `Gift card${t.voucherCode ? ` (${t.voucherCode})` : ''}` : ((t.pgObjectType === 'deposit' || (!IN_SALON_METHODS.includes(t.paymentType) && status !== 4 && status !== 3 && status !== 6)) ? `Deposit: ${PAYMENT_LABELS[t.paymentType] || 'Payment'}` : (PAYMENT_LABELS[t.paymentType] || 'Payment'))}
                        {tipOnLeg(t, transactions, tip) > 0 && ` (incl. ${currency}${tip.toFixed(2)} tip)`}
                        {t.createdAt && moment(t.createdAt).isValid() && <span className="bdp-payment-when"> · {moment(t.createdAt).format('D MMM, HH:mm')}</span>}
                      </span>
                      <span>{currency}{parseFloat(t.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {/* Deposit line — only show when the deposit isn't already
                      represented by a row in transaction[] above. The per-row
                      tagging means most modern bookings have a `Deposit: <method>`
                      line in the methods list above, so showing this would
                      duplicate. Fall back to this line only for bookings whose
                      deposit lives on the booking-level fields without a
                      transaction[] row (legacy data or pending deposits). */}
                  {!!detail.isDeposit && detail.depositAmount > 0 &&
                    !transactions.some(t => t.isDeposit || t.pgObjectType === 'deposit') && (
                    <div className="bdp-payment-row bdp-payment-row--method">
                      <span>Deposit {detail.depositRefunded ? '(refunded)' : (detail.isDepositPay ? '(paid)' : '(pending)')}</span>
                      <span>{currency}{parseFloat(detail.depositAmount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Salon-cancel refund — the deposit went back to the client. */}
                  {!!detail.depositRefunded && detail.depositRefundedAmount > 0 && (
                    <div className="bdp-payment-row bdp-payment-row--discount">
                      <span>Deposit refunded to client</span>
                      <span>-{currency}{parseFloat(detail.depositRefundedAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Refunds on a completed sale (docs/refunds.md), one row each,
                      dated when made. The sale keeps its total. */}
                  {status === 4 && saleRefunds.map((r, i) => (
                    <div key={`rf${i}`} className="bdp-payment-row bdp-payment-row--discount">
                      <span>
                        Refund ({REFUND_METHODS[r.method] || 'other'}){String(r.status).toLowerCase() === 'pending' ? ', pending' : ''}
                        {r.at && moment(r.at).isValid() && <span className="bdp-payment-when"> · {moment(r.at).format('D MMM, HH:mm')}</span>}
                      </span>
                      <span>{'\u2212'}{currency}{Math.abs(parseFloat(r.amount) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {status === 4 && saleRefunds.length === 0 && Math.abs(parseFloat(detail.refundedTotal) || 0) > 0 && (
                    <div className="bdp-payment-row bdp-payment-row--discount">
                      <span>Refunded</span>
                      <span>{'\u2212'}{currency}{Math.abs(parseFloat(detail.refundedTotal)).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Where a completed sale stands: Paid / Part refunded / Refunded. */}
                  {status === 4 && (transactions.length > 0 || refundState !== 'none') && (
                    <div className="bdp-payment-row bdp-payment-row--balance">
                      <span>Status</span>
                      <span>{refundState === 'full' ? 'Refunded' : refundState === 'partial' ? 'Part refunded' : 'Paid'}</span>
                    </div>
                  )}
                  {/* Balance - hide for completed/cancelled/no-show bookings */}
                  {status !== 3 && status !== 4 && status !== 6 && (
                    <div className={`bdp-payment-row bdp-payment-row--balance ${balance > 0.01 ? 'bdp-payment-row--outstanding' : ''}`}>
                      <span>Balance</span>
                      <span>{balance > 0.01 ? `${currency}${balance.toFixed(2)} outstanding` : `${currency}0.00`}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Forms (if any linked to services) */}
          {(() => {
            const allForms = [];
            (detail.servicesBooked || []).forEach(s => {
              (s.linkedForms || []).forEach(f => {
                if (!allForms.find(af => af.formId === f.formId)) {
                  allForms.push(f);
                }
              });
            });
            if (allForms.length === 0) return null;
            const pendingCount = allForms.filter(f => !f.isCompleted).length;
            return (
              <div className="bdp-section">
                <div className="bdp-section-header-row">
                  <h4 className="bdp-section-title">Forms</h4>
                  {pendingCount > 0 && (
                    <span className="bdp-form-badge">{pendingCount} pending</span>
                  )}
                </div>
                <ul className="bdp-form-list">
                  {allForms.map((f, i) => (
                    <li key={i} className="bdp-form-item">
                      {f.isCompleted ? (
                        <svg className="bdp-form-icon bdp-form-icon--done" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" stroke="#007EA0" strokeWidth="1.2"/>
                          <path d="M4.5 7L6.5 9L9.5 5" stroke="#007EA0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg className="bdp-form-icon bdp-form-icon--pending" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" stroke="#ccc" strokeWidth="1.2"/>
                        </svg>
                      )}
                      <span className="bdp-form-name">{f.formName || 'Form'}</span>
                      <span className={`bdp-form-status ${f.isCompleted ? 'bdp-form-status--done' : ''}`}>
                        {f.isCompleted ? 'Completed' : 'Pending'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Notes */}
          {(detail.user?.note || detail.bookingNote) && (
            <div className="bdp-section">
              <h4 className="bdp-section-title">Notes</h4>
              {detail.user?.note && (
                <div className="bdp-note-item">
                  <span className="bdp-note-label">Client</span>
                  <p className="bdp-note">{detail.user.note}</p>
                </div>
              )}
              {detail.bookingNote && (
                <div className="bdp-note-item">
                  <span className="bdp-note-label">Booking</span>
                  <p className="bdp-note">{detail.bookingNote}</p>
                </div>
              )}
            </div>
          )}

          {/* Booking info (expandable) */}
          <div className="bdp-section bdp-section--flush">
            <button
              type="button"
              className="bdp-booking-info-toggle"
              onClick={() => this.setState(prev => ({ showBookingInfo: !prev.showBookingInfo }))}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.1"/>
                <path d="M7 6V10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="7" cy="4.25" r="0.75" fill="currentColor"/>
              </svg>
              <span>Booking details</span>
              <svg className={`bdp-booking-info-chevron ${this.state.showBookingInfo ? 'bdp-booking-info-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {this.state.showBookingInfo && (
              <div className="bdp-booking-info-detail">
                <div className="bdp-info-row">
                  <span className="bdp-info-label">Transaction ID</span>
                  <span className="bdp-info-value" style={{ fontSize: '12px', wordBreak: 'break-all' }}>{detail._id || event.bookingId || ''}</span>
                </div>
                <div className="bdp-info-row">
                  <span className="bdp-info-label">Type</span>
                  <span className="bdp-info-value">
                    {isOnlineBookingDetail(detail, event) ? 'Online' : 'In-salon'}
                  </span>
                </div>
                {detail.createdTime && (
                  <div className="bdp-info-row">
                    <span className="bdp-info-label">Created</span>
                    <span className="bdp-info-value">{moment(detail.createdTime).format('D MMM YYYY, HH:mm')}</span>
                  </div>
                )}
                {detail.updatedTime && detail.updatedTime !== detail.createdTime && (
                  <div className="bdp-info-row">
                    <span className="bdp-info-label">Updated</span>
                    <span className="bdp-info-value">{moment(detail.updatedTime).format('D MMM YYYY, HH:mm')}</span>
                  </div>
                )}
                {!!detail.isDeposit && detail.depositAmount > 0 && (
                  <div className="bdp-info-row">
                    <span className="bdp-info-label">Deposit</span>
                    <span className="bdp-info-value">{currency}{parseFloat(detail.depositAmount).toFixed(2)} {detail.isDepositPay ? '(paid)' : '(pending)'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>}
        </div>

        {/* Footer Actions — when opened from the diary (onOpenCheckout
            is provided), show the full Checkout + More Options menu.
            When opened as a nested panel from the client detail
            (no onOpenCheckout), show a single "Open in diary" button
            so the user can access checkout, edit, cancel, etc. from
            the diary where they all work properly. */}
        <div className="bdp-footer">
          {this.props.onOpenCheckout ? (
            isActionable ? (
              <>
                <div className="bdp-menu-wrap" ref={this._menuRef}>
                  <button className="bdp-btn bdp-btn--secondary bdp-btn--menu" onClick={this.handleMenuToggle}>
                    <span>More Options</span>
                    <svg className={`bdp-menu-chevron ${menuOpen ? 'bdp-menu-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {menuOpen && (
                    <div className="bdp-menu">
                      <button className="bdp-menu-item" onClick={this.handleEdit}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Edit Booking
                      </button>
                      {this.state.series && this.state.series.status === 'active' && (
                        <button className="bdp-menu-item" onClick={this.handleChangeRule}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 3v3h-3M13 8a5 5 0 0 1-8.5 3.5L3 10M3 13v-3h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          Change how often
                        </button>
                      )}
                      {this.state.series && this.state.series.status === 'active' && (
                        <button className="bdp-menu-item" onClick={this.handlePauseRepeating}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4v8M10 4v8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                          Pause
                        </button>
                      )}
                      {this.state.series && this.state.series.status === 'active' && (
                        <button className="bdp-menu-item" onClick={this.handleStopRepeating}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 3v3h-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 12L12 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                          Stop repeating
                        </button>
                      )}
                      <div className="bdp-menu-divider" />
                      <button className="bdp-menu-item bdp-menu-item--danger" onClick={this.handleCancelBooking}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/>
                          <path d="M6 6L10 10M10 6L6 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                        Cancel Booking
                      </button>
                      {/* Fresha parity: no-show only once the appointment has started (BE enforces too). */}
                      {!(this.state.bookingDetail && this.state.bookingDetail.bookingDate && new Date(this.state.bookingDetail.bookingDate).getTime() > Date.now()) && (
                        <button className="bdp-menu-item bdp-menu-item--danger" onClick={this.handleNoShow}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <circle cx="8" cy="12" r="0.75" fill="currentColor"/>
                          </svg>
                          No Show
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button className="bdp-btn bdp-btn--primary" onClick={this.handleCheckout}>Checkout</button>
              </>
            ) : (
              <>
                {showRefund && (
                  <button className="bdp-btn bdp-btn--secondary" onClick={() => this.setState({ refundOpen: true })} style={{ flex: 1 }}>Refund</button>
                )}
                <button className="bdp-btn bdp-btn--primary" onClick={this.handleRebook} style={{ flex: 1 }}>Rebook</button>
              </>
            )
          ) : (
            <>
            {showRefund && (
              <button className="bdp-btn bdp-btn--secondary" onClick={() => this.setState({ refundOpen: true })} style={{ flex: 1 }}>Refund</button>
            )}
            <button className="bdp-btn bdp-btn--primary" onClick={this.handleOpenInDiary} style={{ flex: 1 }}>
              Open in diary
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
            </>
          )}
        </div>
      </>
    );
  }

  handleDeleteEvent = () => {
    this.setState({ activeOverlay: 'deleteEvent', overlayClosing: false });
  }

  executeDeleteEvent = () => {
    const { event, onRefresh, onClose } = this.props;
    if (!event.eventId) return;

    const isHoliday = event.type === 'holiday';
    const userId = localStorage.getItem('userId');
    this.setState({ actionLoading: true });

    const done = () => {
      toast.success(isHoliday ? 'Leave deleted' : 'Event deleted');
      this.setState({ actionLoading: false, activeOverlay: null });
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    };
    const fail = () => {
      toast.error(isHoliday ? 'Could not delete leave' : 'Could not delete event');
      this.setState({ actionLoading: false });
    };

    if (isHoliday) {
      // Strip composite week ID (staffId_YYYY-MM-DD) to get raw staff ID
      const staffId = (event.staffId || userId).split('_')[0];
      import('../../_services/leave.service').then(({ leaveService }) => {
        leaveService.deleteLeave(event.eventId, staffId).then(done).catch(fail);
      });
    } else {
      const rawInfo = event.raw?.list?.[0]?.info || {};
      const rawStaffId = (event.staffId || userId).split('_')[0];
      const eventUserId = rawInfo.userId || rawStaffId;
      import('../../_services/event.service').then(({ eventService }) => {
        eventService.deleteEvent({ hairdresserId: eventUserId, eventId: event.eventId }).then(done).catch(fail);
      });
    }
  }

  renderDeleteEventOverlay() {
    const { event } = this.props;
    const { actionLoading, overlayClosing } = this.state;
    const isHoliday = event?.type === 'holiday';
    return (
      <div className={`cancel-booking-overlay bdp-overlay${overlayClosing ? ' closing' : ''}`} onClick={this.handleCloseOverlay}>
        <div className={`cancel-booking-sheet${overlayClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
          <div className="cancel-booking-header">
            <div className="cancel-booking-header-spacer" />
            <h2 className="cancel-booking-header-title">Delete {isHoliday ? 'Leave' : 'Event'}</h2>
            <button className="cancel-booking-nav-btn" onClick={this.handleCloseOverlay} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="cancel-booking-body">
            <div className="cancel-booking-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#FEE2E2"/>
                <path d="M20 20L36 36M20 36L36 20" stroke="#DC2626" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="cancel-booking-text">
              This will permanently delete this {isHoliday ? 'leave' : 'event'} from the diary. This action cannot be undone.
            </p>
            <div className="cancel-booking-actions">
              <button className="cancel-booking-btn cancel-booking-btn-danger" onClick={this.executeDeleteEvent} disabled={actionLoading}>
                {actionLoading ? 'Deleting...' : `Delete ${isHoliday ? 'Leave' : 'Event'}`}
              </button>
              <button className="cancel-booking-btn cancel-booking-btn-secondary" onClick={this.handleCloseOverlay} disabled={actionLoading}>Go Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  handleEditEventStart = () => {
    const { event } = this.props;
    const raw = event.raw?.list?.[0]?.info || {};
    this.setState({
      editEventMode: true,
      editEventTitle: event.title || '',
      editEventDate: event.date || moment().format('YYYY-MM-DD'),
      editEventEndDate: raw.holidayEndDate || event.date || moment().format('YYYY-MM-DD'),
      editEventStartMinutes: event.startMinutes || 0,
      editEventEndMinutes: event.endMinutes || 0,
      editEventColour: event.diaryColour || '#29282D',
      editEventEmoji: event.emoji || '',
      editEventBookingAllowed: !!raw.isBookingAllowed,
      // Leave type — read from the raw API echo if it's there, otherwise
      // default to 'holiday'. Same enum as NewEventPanel ('holiday' | 'sick' | 'other').
      editEventLeaveType: raw.leaveType || event.leaveType || 'holiday',
      editEventSaving: false,
    });
  }

  handleSaveEvent = () => {
    const { event, onRefresh, onClose } = this.props;
    const { editEventTitle, editEventDate, editEventEndDate, editEventStartMinutes, editEventEndMinutes, editEventColour, editEventBookingAllowed } = this.state;
    const isHoliday = event.type === 'holiday';
    const userId = localStorage.getItem('userId');

    this.setState({ editEventSaving: true });

    if (isHoliday) {
      import('../../_services/leave.service').then(({ leaveService }) => {
        leaveService.updateLeave(event.eventId, {
          hairdresserId: event.staffId || userId,
          startDate: moment(editEventDate).format('YYYY-MM-DD'),
          endDate: moment(editEventEndDate).format('YYYY-MM-DD'),
          title: editEventTitle,
          diaryColour: editEventColour,
          emoji: this.state.editEventEmoji || null,
          // leaveType: 'holiday' | 'sick' | 'other' — persisted on the
          // backend (Joi-validated). Defaults to 'holiday' so partial
          // updates can't accidentally drop the value when the FE state
          // wasn't hydrated.
          leaveType: this.state.editEventLeaveType || 'holiday',
        })
          .then((res) => {
            // The bookings it now lands on, when the server could check
            toast.success(savedWords('Leave updated', affectedFrom(res)), affectedFrom(res) && affectedFrom(res).length ? 6000 : undefined);
            this.setState({ editEventMode: false, editEventSaving: false });
            if (onRefresh) onRefresh();
            if (onClose) onClose();
          })
          .catch(() => { this.setState({ editEventSaving: false }); toast.error('Could not update leave'); });
      });
    } else {
      const rawInfo = event.raw?.list?.[0]?.info || {};
      const eventUserId = rawInfo.userId || event.staffId || userId;
      import('../../_services/event.service').then(({ eventService }) => {
        eventService.editEvent({
          hairdresserId: eventUserId,
          eventId: event.eventId,
          title: editEventTitle,
          date: moment(editEventDate).format('YYYY-MM-DD'),
          startTime: editEventStartMinutes,
          endTime: editEventEndMinutes,
          isBookingAllowed: editEventBookingAllowed ? true : false,
          diaryColour: editEventColour,
          emoji: this.state.editEventEmoji || null,
          repeatType: 'doesNotRepeat',
        })
          .then((res) => {
            const landed = editEventBookingAllowed ? null : affectedFrom(res);
            toast.success(savedWords('Event updated', landed), landed && landed.length ? 6000 : undefined);
            this.setState({ editEventMode: false, editEventSaving: false });
            if (onRefresh) onRefresh();
            if (onClose) onClose();
          })
          .catch(() => { this.setState({ editEventSaving: false }); toast.error('Could not update event'); });
      });
    }
  }

  renderEditEvent() {
    const { event } = this.props;
    const { editEventTitle, editEventDate, editEventEndDate, editEventStartMinutes, editEventEndMinutes, editEventColour, editEventBookingAllowed, editEventSaving } = this.state;
    const isHoliday = event.type === 'holiday';
    const { COLOUR_PRESETS } = require('./eventIllustrations');

    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={() => this.setState({ editEventMode: false })}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Edit {isHoliday ? 'Leave' : 'Event'}</h3>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="bdp-body">
          {/* Leave Type (leave only) — categorises for reporting. */}
          {isHoliday && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Leave Type</label>
              <div className="nep-leave-type">
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.editEventLeaveType === 'holiday' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ editEventLeaveType: 'holiday' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="holiday">🌴</span>
                  Holiday
                </button>
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.editEventLeaveType === 'sick' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ editEventLeaveType: 'sick' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="sick">🤒</span>
                  Sick Day
                </button>
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.editEventLeaveType === 'other' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ editEventLeaveType: 'other' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="other">📋</span>
                  Other
                </button>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Title</label>
            <input type="text" className="bdp-edit-input" style={{ textAlign: 'left' }}
              value={editEventTitle} onChange={(e) => this.setState({ editEventTitle: e.target.value })} maxLength={200} />
          </div>

          {/* Date */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">{isHoliday ? 'Start Date' : 'Date'}</label>
            <div className="bdp-edit-input" ref={el => { this._editEventDateAnchor = el; }}
              onClick={() => this.setState({ datePickerOpen: true })}>
              {moment(editEventDate).format('ddd D MMM YYYY')}
            </div>
            <DiaryDatePicker open={this.state.datePickerOpen} selected={editEventDate}
              anchorEl={this._editEventDateAnchor}
              onChange={(d) => this.setState({ editEventDate: moment(d).format('YYYY-MM-DD'), datePickerOpen: false })}
              onClose={() => this.setState({ datePickerOpen: false })} />
          </div>

          {/* End Date (holiday only) */}
          {isHoliday && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">End Date</label>
              <div className="bdp-edit-input" ref={el => { this._editEventEndDateAnchor = el; }}
                onClick={() => this.setState({ endDatePickerOpen: true })}>
                {moment(editEventEndDate).format('ddd D MMM YYYY')}
              </div>
              <DiaryDatePicker open={this.state.endDatePickerOpen} selected={editEventEndDate}
                anchorEl={this._editEventEndDateAnchor}
                onChange={(d) => this.setState({ editEventEndDate: moment(d).format('YYYY-MM-DD'), endDatePickerOpen: false })}
                onClose={() => this.setState({ endDatePickerOpen: false })} />
            </div>
          )}

          {/* Start/End Time (event only) */}
          {!isHoliday && (
            <div className="bdp-edit-row">
              <div className="bdp-edit-field bdp-edit-field--half">
                <label className="bdp-edit-label">Start Time</label>
                <ShiftTimePicker label="Start Time"
                  selected={(() => { const d = new Date(); d.setHours(Math.floor(editEventStartMinutes / 60), editEventStartMinutes % 60, 0, 0); return d; })()}
                  onChange={(d) => this.setState({ editEventStartMinutes: d.getHours() * 60 + d.getMinutes() })} />
              </div>
              <div className="bdp-edit-field bdp-edit-field--half">
                <label className="bdp-edit-label">Finish Time</label>
                <ShiftTimePicker label="Finish Time"
                  selected={(() => { const d = new Date(); d.setHours(Math.floor(editEventEndMinutes / 60), editEventEndMinutes % 60, 0, 0); return d; })()}
                  onChange={(d) => this.setState({ editEventEndMinutes: d.getHours() * 60 + d.getMinutes() })} />
              </div>
            </div>
          )}

          {/* Colour */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Colour</label>
            <div className="nep-colour-picker">
              {COLOUR_PRESETS.map(c => (
                <button key={c} type="button"
                  className={`nep-colour-swatch ${editEventColour === c ? 'nep-colour-swatch--selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => this.setState({ editEventColour: c })}>
                  {editEventColour === c && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Emoji — same picker as the New Leave / New Event create form
              so editing keeps parity with creating. */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Emoji</label>
            {this.state.editEventEmoji ? (
              <div className="nep-emoji-selected">
                <span className="nep-emoji-selected-icon">{this.state.editEventEmoji}</span>
                <span className="nep-emoji-selected-text">Shows on the diary block</span>
                <button type="button" className="nep-emoji-clear" onClick={() => this.setState({ editEventEmoji: '' })}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="nep-emoji-empty">
                <span className="nep-emoji-empty-text">Pick an emoji for the diary block</span>
              </div>
            )}
            <div className="nep-emoji-suggestions">
              {['✈️', '🏖️', '🌴', '⚽', '💪', '🛍️', '🦷', '🩺', '📚', '👥', '🍽️', '☕', '🎂', '🎉', '📸', '💇', '🏠', '💼', '✨', '🎄'].map(e => (
                <button key={e} type="button"
                  className={`nep-emoji-btn ${this.state.editEventEmoji === e ? 'nep-emoji-btn--selected' : ''}`}
                  onClick={() => this.setState({ editEventEmoji: this.state.editEventEmoji === e ? '' : e })}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Still available for booking (event only) */}
          {!isHoliday && (
            <label className="nep-checkbox-row" onClick={() => this.setState(prev => ({ editEventBookingAllowed: !prev.editEventBookingAllowed }))}>
              <span className={`nep-checkbox ${editEventBookingAllowed ? 'nep-checkbox--checked' : ''}`}>
                {editEventBookingAllowed && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span className="nep-checkbox-label">Still available for booking</span>
            </label>
          )}
        </div>
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={() => this.setState({ editEventMode: false })}>Cancel</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.handleSaveEvent} disabled={editEventSaving}>
            {editEventSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </>
    );
  }

  renderEvent() {
    const { event } = this.props;
    const formattedDate = moment(event.date).format('ddd D MMM YYYY');
    const isHoliday = event.type === 'holiday';
    let eventEmoji = null;
    try { const { matchEventEmoji: match } = require('./eventIllustrations'); eventEmoji = event.emoji || match(event.title); } catch(e) {}

    const duration = event.endMinutes - event.startMinutes;
    const h = Math.floor(duration / 60);
    const m = duration % 60;
    const durLabel = event.isFullDay ? 'All Day' : (h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m} min`);

    // Map leaveType to a small display label for the view. Reads from the
    // raw API echo when present; falls back to 'holiday' so the badge is
    // always shown — backend doesn't echo this field yet (CTO ticket
    // pending). Once the backend ships, the real value will populate.
    const rawLeave = event.raw?.list?.[0]?.info || {};
    const leaveType = rawLeave.leaveType || event.leaveType || 'holiday';
    const leaveTypeLabel = leaveType === 'sick' ? '🤒 Sick Day'
      : leaveType === 'other' ? '📋 Other'
      : '🌴 Holiday';

    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <div className="bdp-avatar bdp-avatar--event" style={isHoliday ? { background: '#E85D75' } : {}}>
              {eventEmoji ? (
                <span style={{ fontSize: 20 }}>{eventEmoji}</span>
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="2" y="3" width="14" height="12" rx="2" stroke="#fff" strokeWidth="1.5"/>
                  <path d="M2 7H16" stroke="#fff" strokeWidth="1.5"/>
                  <path d="M6 1V4M12 1V4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">{event.title}</h3>
              <span className="bdp-client-sub">{isHoliday ? 'Leave' : 'Event'}</span>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="bdp-body">
          <div className="bdp-meta-row">
            <span className="bdp-meta-date">{formattedDate}</span>
          </div>
          {/* Leave-type badge — sits right under the date so it's the first
              piece of context after the title. Defaults to Holiday until
              backend echo lands. */}
          {isHoliday && (
            <div className="bdp-meta-row">
              <span className="bdp-leave-type-badge">{leaveTypeLabel}</span>
            </div>
          )}
          <div className="bdp-time-row">
            <svg className="bdp-time-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M7 3.5V7L9.5 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span>{event.isFullDay ? 'All Day' : `${formatTime(event.startMinutes)} - ${formatTime(event.endMinutes)}`}</span>
            <span className="bdp-duration">{durLabel}</span>
          </div>

          {/* Staff */}
          {event.staffId && this.props.staffList && (
            <div className="bdp-staff-row">
              <svg className="bdp-staff-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M2 12.5C2 10.29 4.24 8.5 7 8.5C9.76 8.5 12 10.29 12 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span>{this.props.staffList.find(s => s.id === event.staffId)?.name || ''}</span>
            </div>
          )}
          {/* Booking-availability indicator (events only). Mirrors the
              corner icon on the diary block: if the slot is blocked, show
              the same do-not-disturb SVG inline with text so the salon
              owner sees the state at a glance. */}
          {!isHoliday && event.isBookingAllowed === false && (
            <div className="bdp-staff-row">
              <span className="bdp-noblock-icon" aria-hidden="true">
                <BlockIcon />
              </span>
              <span>Not available for booking</span>
            </div>
          )}
        </div>
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary bdp-btn--danger" onClick={this.handleDeleteEvent}>Delete</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.handleEditEventStart}>Edit</button>
        </div>
      </>
    );
  }

  renderBreak() {
    const { event } = this.props;
    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <div className="bdp-avatar bdp-avatar--break">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M6 3V9L3 12H15L12 9V3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 15H14" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Break</h3>
              <span className="bdp-client-sub">{formatTime(event.startMinutes)} - {formatTime(event.endMinutes)}</span>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </>
    );
  }

  renderEditClientPicker() {
    const { editClientSearchQuery, editClientResults, editClientSearching } = this.state;
    const WALKIN_CLIENT_ID_LOCAL = '64f5c7789a069cda0188fea3';
    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={() => this.setState({ editScreen: 'form', editClientSearchQuery: '', editClientResults: [] })}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Change Client</h3>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="bdp-picker-search">
          <svg className="bdp-picker-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input type="text" className="bdp-picker-search-input" placeholder="Search by name, email, or phone..."
            value={editClientSearchQuery} onChange={(e) => this.handleEditClientSearch(e.target.value)} autoFocus={window.innerWidth >= 768} />
        </div>
        <div className="bdp-picker-list bdp-picker-list--fullscreen" onScroll={this.handleEditClientScroll}>
          <button type="button" className="nbp-client-picker-item nbp-client-picker-item--walkin"
            onClick={() => this.setState({ editClientId: WALKIN_CLIENT_ID_LOCAL, editClientName: 'Walk-in', editClientEmail: '', editClientPhone: '', editScreen: 'form', editClientResults: [] })}>
            <div className="nbp-client-picker-avatar nbp-client-picker-avatar--walkin">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3C6.9 3 6 3.9 6 5C6 6.1 6.9 7 8 7C9.1 7 10 6.1 10 5C10 3.9 9.1 3 8 3Z" fill="currentColor"/>
                <path d="M8 8.5C5.79 8.5 4 9.79 4 11.5V13H12V11.5C12 9.79 10.21 8.5 8 8.5Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="nbp-client-picker-info">
              <span className="nbp-client-picker-name">Walk-in Client</span>
            </div>
          </button>
          {/* Add new client */}
          <button type="button" className="nbp-client-picker-item nbp-client-picker-item--add" onClick={() => this.setState({ showAddClientFromEdit: true })}>
            <div className="nbp-client-picker-avatar nbp-client-picker-avatar--add">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="nbp-client-picker-info">
              <span className="nbp-client-picker-name">New Client</span>
              <span className="nbp-client-picker-sub">Create a new client profile</span>
            </div>
          </button>
          {editClientSearching && <div className="bdp-picker-loading">Searching...</div>}
          {!editClientSearching && editClientSearchQuery.trim() && editClientResults.length === 0 && (
            <div className="bdp-picker-loading">No clients found</div>
          )}
          {editClientResults.map((c, i) => (
            <button key={i} type="button" className="nbp-client-picker-item"
              onClick={() => this.setState({ editClientId: c._id, editClientName: c.name, editClientEmail: c.email || '', editClientPhone: c.phoneNumber || '', editScreen: 'form', editClientResults: [] })}>
              <div className="nbp-client-picker-avatar">{(c.name || '?')[0]}</div>
              <div className="nbp-client-picker-info">
                <span className="nbp-client-picker-name">{c.name}</span>
                {c.email && !c.email.includes('@beuapp.com') && <span className="nbp-client-picker-sub">{c.email}</span>}
                {c.phoneNumber && <span className="nbp-client-picker-sub">{c.phoneNumber}</span>}
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  renderEditMode() {
    const screen = this.state.editScreen || 'form';

    if (screen === 'servicePicker') {
      return <div className="bdp-screen-slide-in" key="servicePicker">{this.renderServicePickerScreen()}</div>;
    }
    if (screen === 'clientPicker') {
      return <div className="bdp-screen-slide-in" key="clientPicker">{this.renderEditClientPicker()}</div>;
    }
    return this.renderEditForm();
  }

  renderEditForm() {
    const { event, staffList, currencySymbol: propCurrency } = this.props;
    const { bookingDetail, editDate, editStartMinutes, editStaffId, editSaving, editServices } = this.state;
    const detail = bookingDetail || {};

    const clientName = detail.user?.name || event.clientName || 'Client';
    const currency = detail.currencySymbol || detail.hairdresser?.hairdresser?.currencySymbol || propCurrency || '£';
    const totalAmount = editServices.reduce((sum, s) => sum + (parseFloat(s.servicePrice) || 0), 0);

    return (
      <>
        {/* Header */}
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={this.handleEditCancel}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Edit Booking</h3>
              <span className="bdp-client-sub">{clientName}</span>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Edit Form */}
        <div className="bdp-body bdp-edit-body">
          {/* Date */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Date</label>
            <button
              type="button"
              className="bdp-edit-input bdp-edit-date-btn"
              ref={el => { this._dateAnchor = el; }}
              onClick={() => this.setState({ datePickerOpen: true })}
            >
              {moment(editDate).format('ddd D MMM YYYY')}
            </button>
            <DiaryDatePicker
              selected={new Date(editDate)}
              open={this.state.datePickerOpen}
              anchorEl={this._dateAnchor}
              onChange={(date) => {
                this.setState({
                  editDate: moment(date).format('YYYY-MM-DD'),
                  datePickerOpen: false,
                });
              }}
              onClose={() => this.setState({ datePickerOpen: false })}
            />
          </div>

          {/* Start Time */}
          <div className="bdp-edit-row">
            <div className="bdp-edit-field bdp-edit-field--half">
              <label className="bdp-edit-label">Start Time</label>
              <ShiftTimePicker
                label="Start Time"
                selected={(() => { const d = new Date(); d.setHours(Math.floor(editStartMinutes / 60), editStartMinutes % 60, 0, 0); return d; })()}
                onChange={(date) => this.setState({ editStartMinutes: date.getHours() * 60 + date.getMinutes() })}
              />
            </div>
          </div>

          {/* Staff (booking-level until backend supports per-service hairdresserId) */}
          {staffList && staffList.length > 0 && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Staff Member</label>
              <StaffPicker
                label="Staff Member"
                staffList={staffList}
                value={editStaffId}
                onChange={(id) => this.setState({ editStaffId: id })}
              />
            </div>
          )}

          {/* Client */}
          <div className="bdp-section">
            <h4 className="bdp-section-title">Client</h4>
            <button
              type="button"
              className="nbp-selected-client bdp-client-tappable"
              onClick={() => { this.setState({ editScreen: 'clientPicker', editClientSearchQuery: '' }); this.handleEditClientSearch(''); }}
            >
              <div className="nbp-selected-client-info">
                <span className="nbp-selected-client-name">{this.state.editClientName || clientName}</span>
                {this.state.editClientEmail && !this.state.editClientEmail.includes('@beuapp.com') && (
                  <span className="nbp-selected-client-sub">{this.state.editClientEmail}</span>
                )}
                {this.state.editClientPhone && (
                  <span className="nbp-selected-client-sub">{this.state.editClientPhone}</span>
                )}
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="#c7c7cc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Services (timeline) */}
          <div className="bdp-section">
            <div className="bdp-edit-section-header">
              <h4 className="bdp-section-title">Services</h4>
            </div>
            {editServices.length === 0 && (
              <p className="bdp-edit-empty">No services added</p>
            )}
            {/* Setup buffer pill — multi-service rule, lives on first svc only. */}
            {editServices.length > 0 && (
              (editServices[0].setupTime || 0) > 0 ? (
                <div className="bdp-processing-spacer">
                  <span className="bdp-processing-spacer-line" />
                  <div className="bdp-processing-spacer-content">
                    <span className="bdp-processing-spacer-label">Setup</span>
                    <div className="bdp-processing-input-wrap">
                      <DurationPicker
                        label="Setup Time"
                        value={editServices[0].setupTime}
                        onChange={(mins) => this.handleEditSetupTimeChange(mins)}
                      />
                      <button type="button" className="bdp-processing-remove"
                        onClick={() => this.handleEditSetupTimeChange(0)} title="Remove setup time">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="bdp-processing-spacer-line" />
                </div>
              ) : (
                <div className="bdp-processing-spacer bdp-processing-spacer--add">
                  <button type="button" className="bdp-add-processing-pill"
                    onClick={() => this.handleEditSetupTimeChange(15)}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    Setup time
                  </button>
                </div>
              )
            )}
            <div className="bdp-service-blocks">
              {editServices.map((s, i) => {
                // First service starts after the setup buffer.
                let serviceStartMin = editStartMinutes + (editServices[0].setupTime || 0);
                for (let j = 0; j < i; j++) {
                  serviceStartMin += (editServices[j].serviceTime || 0) + (editServices[j].processingTime || 0)
                    + (editServices[j].finishingTime || 0);
                }
                const svcColour = s.service?.diaryColour || s.diaryColour || event.diaryColour;
                // A service with finishing time keeps its processing (apply →
                // processing → finish), even as the last/only service.
                const hasFinishing = (s.finishingTime || 0) > 0;
                return (
                  <React.Fragment key={i}>
                    <div className={`bdp-svc-card ${s._needsReplacement ? 'bdp-svc-card--invalid' : ''}`}>
                      {/* Row 1: Service name - tap to change */}
                      <button type="button" className="bdp-svc-card-header" onClick={() => this.handleOpenServicePicker(i)}>
                        <span className="bdp-svc-card-color" style={{ backgroundColor: s._needsReplacement ? '#E85D75' : svcColour }} />
                        <span className="bdp-svc-card-name">
                          {s._needsReplacement
                            ? <>{s.service?.serviceName || s.serviceName || 'Service'}</>
                            : (s.service?.serviceName || s.serviceName || s.service?.name)
                          }
                        </span>
                        <svg className="bdp-svc-card-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {s._needsReplacement && (
                        <div className="bdp-svc-card-warning">
                          Not offered by this stylist. Tap to change.
                        </div>
                      )}

                      {/* Row 2: Timing + Duration */}
                      <div className="bdp-svc-card-fields">
                        <div className="bdp-svc-card-field">
                          <label className="bdp-svc-card-label">Timing</label>
                          <div className="bdp-svc-card-value">{formatTime(serviceStartMin)} - {formatTime(serviceStartMin + (s.serviceTime || 0))}</div>
                        </div>
                        <div className="bdp-svc-card-field">
                          <label className="bdp-svc-card-label">Duration</label>
                          <DurationPicker
                            label="Duration"
                            value={s.serviceTime || 30}
                            onChange={(mins) => this.handleServiceDurationChange(i, mins)}
                          />
                        </div>
                      </div>

                      {/* Row 3: Price */}
                      <div className="bdp-svc-card-fields">
                        <div className="bdp-svc-card-field bdp-svc-card-field--full">
                          <label className="bdp-svc-card-label">Price</label>
                          <div className="bdp-svc-card-value">{currency}{parseFloat(s.servicePrice || 0).toFixed(2)}</div>
                        </div>
                      </div>

                      {/* MULTI-STYLIST: Per-service staff picker - enable when backend supports hairdresserId per service */}

                      {/* Row 4: Actions (only if multiple services) */}
                      {editServices.length > 1 && (
                        <div className="bdp-svc-card-actions">
                          <button type="button" className="bdp-svc-card-btn" disabled={i === 0}
                            onClick={() => this.handleMoveService(i, -1)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 7.5L6 3.5L10 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Move up
                          </button>
                          <button type="button" className="bdp-svc-card-btn" disabled={i === editServices.length - 1}
                            onClick={() => this.handleMoveService(i, 1)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Move down
                          </button>
                          <button type="button" className="bdp-svc-card-btn bdp-svc-card-btn--remove"
                            onClick={() => this.handleRemoveService(i)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                            </svg>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Processing time spacer between services, or before finishing */}
                    {i < editServices.length - 1 || hasFinishing ? (
                      s.processingTime > 0 ? (
                        <div className="bdp-processing-spacer">
                          <span className="bdp-processing-spacer-line" />
                          <div className="bdp-processing-spacer-content">
                            <span className="bdp-processing-spacer-label">Processing</span>
                            <div className="bdp-processing-input-wrap">
                              <DurationPicker
                                label="Processing Time"
                                value={s.processingTime}
                                onChange={(mins) => this.handleProcessingTimeChange(i, mins)}
                              />
                              <button type="button" className="bdp-processing-remove"
                                onClick={() => this.handleProcessingTimeChange(i, 0)} title="Remove processing time">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                          <span className="bdp-processing-spacer-line" />
                        </div>
                      ) : (
                        <div className="bdp-processing-spacer bdp-processing-spacer--add">
                          <button type="button" className="bdp-add-processing-pill"
                            onClick={() => this.handleProcessingTimeChange(i, 15)}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                            Processing time
                          </button>
                        </div>
                      )
                    ) : null}
                    {hasFinishing && (
                      <div className="bdp-processing-spacer">
                        <span className="bdp-processing-spacer-line" />
                        <div className="bdp-processing-spacer-content">
                          <span className="bdp-processing-spacer-label">Finishing</span>
                          <div className="bdp-processing-input-wrap">
                            <DurationPicker
                              label="Finishing Time"
                              value={s.finishingTime}
                              onChange={(mins) => this.handleFinishingTimeChange(i, mins)}
                            />
                            <button type="button" className="bdp-processing-remove"
                              onClick={() => this.handleFinishingTimeChange(i, 0)} title="Remove finishing time">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <span className="bdp-processing-spacer-line" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Cleanup buffer pill — multi-service rule, lives on last svc only. */}
            {editServices.length > 0 && (
              (editServices[editServices.length - 1].cleanupTime || 0) > 0 ? (
                <div className="bdp-processing-spacer">
                  <span className="bdp-processing-spacer-line" />
                  <div className="bdp-processing-spacer-content">
                    <span className="bdp-processing-spacer-label">Cleanup</span>
                    <div className="bdp-processing-input-wrap">
                      <DurationPicker
                        label="Cleanup Time"
                        value={editServices[editServices.length - 1].cleanupTime}
                        onChange={(mins) => this.handleEditCleanupTimeChange(mins)}
                      />
                      <button type="button" className="bdp-processing-remove"
                        onClick={() => this.handleEditCleanupTimeChange(0)} title="Remove cleanup time">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="bdp-processing-spacer-line" />
                </div>
              ) : (
                <div className="bdp-processing-spacer bdp-processing-spacer--add">
                  <button type="button" className="bdp-add-processing-pill"
                    onClick={() => this.handleEditCleanupTimeChange(15)}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    Cleanup time
                  </button>
                </div>
              )
            )}
            {/* Add service button */}
            <button type="button" className="bdp-edit-add-btn bdp-edit-add-btn--below" onClick={() => this.handleOpenServicePicker(null)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Add Service
            </button>
            {/* Finish time + total */}
            {editServices.length > 0 && (
              <div className="bdp-edit-summary-block">
                <div className="bdp-edit-finish-summary">
                  <span className="bdp-finish-label">Finishes at</span>
                  <span className="bdp-finish-time">{formatTime(this.getEditEndMinutes())}</span>
                  <span className="bdp-finish-duration">{(() => { const t = this.getEditTotalDuration(); const h = Math.floor(t / 60); const m = t % 60; if (h === 0) return `${m}m total`; if (m === 0) return `${h}h total`; return `${h}h ${m}m total`; })()}</span>
                </div>
                <div className="bdp-total-row">
                  <span>Total</span>
                  <span className="bdp-total-amount">{currency}{parseFloat(totalAmount).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* A repeat: this visit, or this and the later ones (with the count
            and the last date once the server lists the visits) */}
        {detail.seriesId && this.state.series && this.state.series.status === 'active' && (() => {
          const w = editWords(this.state.editScope, visitsAround(this.state.series, this.props.event?.bookingId));
          return <ScopeSwitch value={this.state.editScope} onChange={(editScope) => this.setState({ editScope })} laterLabel={w.later} line={w.line} />;
        })()}

        {/* Notify client */}
        {(() => {
          const isWalkIn = !this.state.editClientId || this.state.editClientId === '64f5c7789a069cda0188fea3';
          if (isWalkIn) return null;
          return (
            <div className="bdp-notify-row" onClick={() => this.setState(prev => ({ notifyEmail: !prev.notifyEmail }))}>
              <span className="bdp-notify-label">Email update to client</span>
              <div className={`bdp-notify-toggle ${this.state.notifyEmail ? 'bdp-notify-toggle--on' : ''}`}>
                <span className="bdp-notify-toggle-knob" />
              </div>
            </div>
          );
        })()}


        {/* Footer */}
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={this.handleEditCancel}>Cancel</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.handleEditSave} disabled={editSaving || (editServices && editServices.some(s => s._needsReplacement))}>
            {this.state.followChecking ? 'Checking the later visits...' : editSaving ? 'Saving...' : (detail.seriesId && this.state.series && this.state.series.status === 'active' ? editWords(this.state.editScope, visitsAround(this.state.series, this.props.event?.bookingId)).button : 'Save Changes')}
          </button>
        </div>
      </>
    );
  }

  renderServicePickerScreen() {
    const { currencySymbol: propCurrency } = this.props;
    const { bookingDetail, editServices, serviceSearchQuery, availableServices, servicesLoading, replacingServiceIndex } = this.state;
    const detail = bookingDetail || {};
    const currency = detail.currencySymbol || detail.hairdresser?.hairdresser?.currencySymbol || propCurrency || '£';
    const pickerTitle = replacingServiceIndex !== null ? 'Change Service' : 'Add Service';

    return (
      <>
        {/* Header */}
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={this.handleCloseServicePicker}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">{pickerTitle}</h3>
            </div>
          </div>
          <button className="beu-icon-btn" onClick={this.props.onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Sticky search */}
        <div className="bdp-picker-search">
          <svg className="bdp-picker-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="bdp-picker-search-input"
            placeholder="Search services..."
            value={serviceSearchQuery}
            onChange={(e) => this.setState({ serviceSearchQuery: e.target.value })}
            autoFocus={window.innerWidth >= 768}
          />
          {serviceSearchQuery && (
            <button type="button" className="bdp-picker-search-clear" onClick={() => this.setState({ serviceSearchQuery: '' })}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M4 4L10 10M10 4L4 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Full-height scrollable list */}
        <div className="bdp-picker-list bdp-picker-list--fullscreen">
          {servicesLoading ? (
            <div className="bdp-picker-loading">Loading services...</div>
          ) : (
            (() => {
              const query = serviceSearchQuery.toLowerCase().trim();
              let hasResults = false;
              const content = availableServices.map((cat, ci) => {
                const catServices = cat.hairdresserService || cat.services || [];
                const filtered = query
                  ? catServices.filter(s => (s.serviceName || '').toLowerCase().includes(query))
                  : catServices;
                if (filtered.length === 0) return null;
                hasResults = true;
                return (
                  <div key={ci} className="bdp-picker-category">
                    {!query && <div className="bdp-picker-category-label">{cat.categoryName || 'Services'}</div>}
                    {filtered.map((svc, si) => {
                      const svcId = svc._id || svc.serviceId;
                      const alreadyAdded = editServices.some(es =>
                        (es.service?.serviceId || es.serviceId) === svcId
                      );
                      return (
                        <button
                          key={si}
                          type="button"
                          className={`bdp-picker-item ${alreadyAdded ? 'bdp-picker-item--added' : ''}`}
                          onClick={() => !alreadyAdded && this.handleSelectService(svc)}
                          disabled={alreadyAdded}
                        >
                          <span className="bdp-picker-item-color" style={{ background: svc.diaryColour || svc.service?.diaryColour || '#c7c7cc' }} />
                          <div className="bdp-picker-item-info">
                            <span className="bdp-picker-item-name">{svc.serviceName}</span>
                            <span className="bdp-picker-item-meta">{svc.serviceTime} min</span>
                          </div>
                          <span className="bdp-picker-item-price">{currency}{parseFloat(svc.servicePrice || 0).toFixed(2)}</span>
                          {alreadyAdded && (
                            <svg className="bdp-picker-item-check" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#007EA0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              });
              if (!hasResults) {
                return <div className="bdp-picker-empty">{query ? 'No services match your search' : 'No services available'}</div>;
              }
              return content;
            })()
          )}
        </div>
      </>
    );
  }

  loadRoomOptions = () => {
    const axios = require('axios');
    const API = require('../../config/API').default;
    axios.post(API.RESOURCE_BOOKING_OPTIONS, { bookingId: this.props.event.bookingId },
      { headers: { accessToken: localStorage.getItem('accessToken'), 'Content-Type': 'application/json' } })
      .then(res => this.setState({ roomOptions: res?.data?.responseData?.groups || [] }))
      .catch(() => this.setState({ roomOptions: [] }));
  };

  saveRoomChange = (groupId, resourceId, pinned) => {
    const axios = require('axios');
    const API = require('../../config/API').default;
    const toastMod = require('../shared/Toast').default;
    this.setState({ roomSaving: true });
    axios.put(API.RESOURCE_BOOKING_ASSIGNMENT, { bookingId: this.props.event.bookingId, groupId, resourceId, pinned },
      { headers: { accessToken: localStorage.getItem('accessToken'), 'Content-Type': 'application/json' } })
      .then(res => {
        this.setState({ roomSaving: false });
        if (res?.data?.statusCode === 1) {
          toastMod.success('Room updated');
          this.setState({ roomExpanded: null, roomOptions: null });
          if (this.props.onRefresh) this.props.onRefresh();
        } else {
          toastMod.error(res?.data?.error?.responseMessage || "Couldn't move the booking");
        }
      })
      .catch(e => {
        this.setState({ roomSaving: false });
        toastMod.error(e?.response?.data?.error?.responseMessage || "Couldn't move the booking");
      });
  };

  render() {
    const { event } = this.props;
    if (!event) return null;

    let content;
    if (this.state.ruleEdit && event.type === 'booking') {
      content = this.renderRuleEdit();
    } else if (this.state.editMode && event.type === 'booking' && this.state.followCheck) {
      content = this.renderFollowCheck();
    } else if (this.state.editMode && event.type === 'booking') {
      content = this.renderEditMode();
    } else if (this.state.isLoading) {
      content = this.renderSkeleton();
    } else if (this.state.error) {
      content = this.renderError();
    } else if (event.type === 'booking') {
      content = this.renderBooking();
    } else if (event.type === 'break') {
      content = this.renderBreak();
    } else {
      content = this.state.editEventMode ? this.renderEditEvent() : this.renderEvent();
    }

    // Tucked away while one later visit is placed on the diary
    if (this.state.tucked) {
      return (
        <button type="button" className="nbp-minimised" onClick={() => { cancelPlacement({ quiet: true }); this.setState({ tucked: false }); }}>
          <span className="nbp-minimised-label">Check the later visits{this.state.bookingDetail && this.state.bookingDetail.user && this.state.bookingDetail.user.name ? ` for ${this.state.bookingDetail.user.name}` : ''}</span>
        </button>
      );
    }

    return (
      <>
        <SlidePanel
          ref={this.slidePanelRef}
          onClose={this.props.onClose}
          className={`bdp-panel${this.props.nested ? ' bdp-panel--nested' : ''}`}
          width={420}
          zIndex={this.props.nested ? 100010 : undefined}
        >
          {content}
        </SlidePanel>
        {this.state.refundOpen && this.state.bookingDetail && (
          <RefundPanel
            bookingId={this.state.bookingDetail._id}
            clientName={this.state.bookingDetail.user && this.state.bookingDetail.user._id !== WALKIN_CLIENT_ID ? this.state.bookingDetail.user.name : null}
            clientEmail={this.state.bookingDetail.user && this.state.bookingDetail.user._id !== WALKIN_CLIENT_ID ? this.state.bookingDetail.user.email : null}
            onClose={() => this.setState({ refundOpen: false })}
            onRefunded={this.handleRefunded}
          />
        )}
        {this.state.activeOverlay === 'cancel' && ReactDOM.createPortal(this.renderCancelOverlay(), document.body)}
        {this.state.activeOverlay === 'noshow' && ReactDOM.createPortal(this.renderNoShowOverlay(), document.body)}
        {this.state.activeOverlay === 'deleteEvent' && ReactDOM.createPortal(this.renderDeleteEventOverlay(), document.body)}
        {this.state.activeOverlay === 'stopRepeat' && ReactDOM.createPortal(this.renderStopRepeatOverlay(), document.body)}
        {this.state.activeOverlay === 'pauseRepeat' && ReactDOM.createPortal(this.renderPauseOverlay(), document.body)}
        {this.state.showSendForm && ReactDOM.createPortal(
          <div className="bdp-send-form-overlay" onClick={() => this.setState({ showSendForm: false })}>
            <div className="bdp-send-form-modal" onClick={e => e.stopPropagation()}>
              <div className="bdp-send-form-header">
                <div>
                  <h3 className="bdp-send-form-title">Add Form</h3>
                  <p className="bdp-send-form-subtitle">Select a form to attach to this client</p>
                </div>
                <button className="beu-icon-btn" onClick={() => this.setState({ showSendForm: false })}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <div className="bdp-send-form-body">
                {this.state.availableFormsLoading ? (
                  <div className="bdp-tab-loading"><div className="bdp-loading-spinner" /><span>Loading forms...</span></div>
                ) : this.state.availableForms.length === 0 ? (
                  <p className="bdp-tab-empty">No active forms available</p>
                ) : (
                  this.state.availableForms.map(form => (
                    <div key={form._id} className="bdp-send-form-item">
                      <svg className="bdp-send-form-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M9 1H4C3.45 1 3 1.45 3 2V14C3 14.55 3.45 15 4 15H12C12.55 15 13 14.55 13 14V5L9 1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                        <path d="M9 1V5H13" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                      </svg>
                      <div className="bdp-send-form-info">
                        <span className="bdp-send-form-name">{form.name || 'Untitled Form'}</span>
                        <span className="bdp-send-form-meta">
                          {form.sections ? form.sections.length + ' section' + (form.sections.length !== 1 ? 's' : '') : 'Form'}
                        </span>
                      </div>
                      <div className="bdp-send-form-actions">
                        <button
                          type="button"
                          className="bdp-form-action-btn"
                          onClick={() => {
                            this.setState({ showSendForm: false });
                            const clientId = this.state.bookingDetail?.user?._id || '';
                            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
                            if (this.props.history) this.props.history.push('/forms/fill/' + form._id + '?clientId=' + clientId + '&returnUrl=' + returnUrl);
                          }}
                        >
                          Fill Now
                        </button>
                        <button
                          type="button"
                          className="bdp-form-action-btn bdp-form-action-btn--send"
                          disabled={this.state.sendingFormId === form._id}
                          onClick={() => this.handleSendForm(form._id)}
                        >
                          {this.state.sendingFormId === form._id ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
        {this.state.showAddClientFromEdit && (
          <AddClientPanel
            onClose={() => this.setState({ showAddClientFromEdit: false })}
            onClientAdded={(newClient) => {
              if (newClient && newClient.name) {
                this.setState({
                  showAddClientFromEdit: false,
                  editScreen: 'form',
                  editClientId: newClient._id || '',
                  editClientName: newClient.name,
                  editClientEmail: newClient.email || '',
                  editClientPhone: newClient.phoneNumber || '',
                  editClientResults: [],
                });
              } else {
                this.setState({ showAddClientFromEdit: false });
              }
            }}
            history={this.props.history}
          />
        )}
        {this.state.clientPanelOpen && this.state.bookingDetail?.user?._id && !isReceptionHidden(['clients', 'clientDetails']) && (
          <ClientDetailPanel
            key={this.state.bookingDetail.user._id + (this.state.clientPanelInitialTab || '')}
            clientId={this.state.bookingDetail.user._id}
            initialTab={this.state.clientPanelInitialTab}
            onClose={() => this.setState({ clientPanelOpen: false, clientPanelInitialTab: null })}
            history={this.props.history}
            user={this.props.user}
          />
        )}
      </>
    );
  }
}

export default BookingDetailPanel;
